import { MarkerConnectionsLayer } from './markers/MarkerConnectionsLayer'
import { TerritoryMarker } from './markers/TerritoryMarker'
import { territoryMarkerMetrics, opaqueMarkerFill } from './markers/geometry'
/**
 * MapView — renders a real classic-style continent map using SVG polygons.
 *
 * For each territory the engine emits an SVG path (`shape`) describing
 * its polygon outline. Polygons share borders by sharing vertices, so the
 * map looks like a divided continent rather than dots-with-lines. Each
 * territory fills with the owner's color (clear/neutral when unowned),
 * carries a visible stroke for the border, and shows a circular troop-
 * count badge at the centroid (`position`).
 *
 * Selection states:
 *   - Selected territory: gold/yellow stroke, thicker.
 *   - Valid target (attack or fortify destination): dashed amber stroke.
 *
 * The SVG viewBox is read from `state.map.viewBox` so each map can use
 * its own canvas. Paths that reference absolute coordinates from a
 * source SVG (e.g. Wikipedia's classic board) use viewBox.x/y to position
 * the crop window. Container scales the SVG to fit the screen.
 *
 * testID="territory-<id>" on each territory group for Playwright + a11y.
 */

import React from 'react'
import { View, PanResponder, Platform, Text, TouchableOpacity, Animated, useWindowDimensions } from 'react-native'
import { heavyNumberFont } from '../../lib/heavyNumberFont'
import Svg, { Path, Circle, Rect, Text as SvgText, G, Line, Polygon, Polyline, Defs, ClipPath, Pattern, Image as SvgImage } from 'react-native-svg'
import { MAP_BACKGROUND_IMAGES } from '../../lib/mapBackgrounds'
import { MapDecorations, splitDecorations } from './MapDecorations'
import { WarfishClassicsAboutModal } from './warfish/WarfishClassicsHelp'
import { panNativeProps, panTransform } from '../../lib/mapPanTransform'
import {
  clampZoomScale,
  panFromScreenDelta,
  rescaleAboutCenter,
  DEFAULT_ZOOM_SCALE,
  MIN_ZOOM_SCALE,
  MAX_ZOOM_SCALE,
  ZOOM_STEP,
  type PanOffset,
} from '../../lib/mapZoom'
import { consumeDragHint } from '../../lib/dragHint'
import { lightenHex, illuminateHex, mixHex } from '../../lib/colorAdjust'
import { foggedTerritoryIds, HEAVY_FOG_FILL, HEAVY_FOG_LINE } from '../../lib/fogStyle'
import { FogHatchPattern } from './FogHatchPattern'
import { FogLiftLayer, type FogLiftShape } from './FogLiftLayer'
import {
  applyBadgeAnchors,
  closestVertexPair,
  computeAlignedBadgePositions,
  contrastTextColor,
  lineCrossesPolygon,
  parsePolygonVertices,
  polygonAreaFromD,
  polygonsShareEdge,
  forcedCrossingSegment,
  regionBadgeAnchor,
} from './badgeHelpers'
import { assignRegionColors, buildRegionAdjacency, type RegionId } from './regionColors'
import {
  buildCanonicalPairSet,
  buildSeaRouteCacheKey,
  canUseBaseViewBoxSeaRoutes,
  computeSeaRoutes,
  customDiceRouteKinds,
  oneWayArrowPoints,
  clearRouteEnds,
  outlineRings,
  routeLineEnd,
  routePairKey,
  type SeaRoute,
} from './seaRouteHelpers'
import { borderChevronHaloWidth, borderChevronPoints, computeBorderChevrons } from './borderChevrons'
import { MAP_SYMBOL_COLORS, mapSymbolColor } from './mapSymbols'
import { MapLegend } from './MapLegend'
import { mapContentInsets, mapYToWrapper } from './mapLegendLayout'
import { PLAYER_COLORS, clampPlayerSlot } from '@warcarp/ui'
import { useColorBySlot } from '../../lib/playerColor'
import { getBundledMap } from '../../lib/bundledMaps'
// Import HydratedGameState (includes map: MapDef) aliased as GameState so
// MapView always receives fully-resolved map geometry. The wire GameState
// from @warcarp/api-types has only mapId; use HydratedGameState here.
import type { HydratedGameState as GameState, TerritoryId } from '../../lib/gameState'
import { mapGeometryVersion } from '@warcarp/api-types'
import type { GameEvent } from '@warcarp/api-types'
import { useMapDimensions } from '../../lib/useMapDimensions'
import { mapVerticalTrim } from '../../lib/mapContentBounds'
import { useTheme } from '../../hooks/useTheme'
import type { ConquestAnimationEntry } from '../../hooks/useConquestEvents'
import { usePlacementEvents } from '../../hooks/usePlacementEvents'
import { useAttackEvents, type AttackAnimationEntry } from '../../hooks/useAttackEvents'
import { useFortifyEvents, type FortifyAnimationEntry } from '../../hooks/useFortifyEvents'
import { ConquestFlashOverlays, ConquestRingLayer } from './ConquestFlashLayer'
import { PlacementBadgeLayer } from './PlacementBadgeLayer'
import { AttackBadgeLayer } from './AttackBadgeLayer'
import { AttackArrowLayer } from './AttackArrowLayer'
import { FortifyTrailLayer } from './FortifyTrailLayer'
import { SourceHatchLayer } from './SourceHatchLayer'
import { TargetHaloLayer } from './TargetHaloLayer'
import { mapInnerGlowEnabled } from '../../lib/mapInnerGlowEnabled'
import { SelectionDimLayer } from './SelectionDimLayer'
import { MapOverlayLayer, useOverlayFade, type MapOverlayFrame } from './MapOverlayLayer'
import { SelectionEmphasisLayer, type SelectionEmphasisTarget } from './SelectionEmphasisLayer'
import { resolveBackdropFillOpacity, resolveSpotlightDimColor } from './backdropFill'
import { getSelectionDimmedTerritoryIds } from '../../lib/selectionDimming'
import { mapViewPropsEqual } from '../../lib/mapViewPropsEqual'
import { collectDeviceInfo } from '../../lib/deviceInfo'
import { reportMessage } from '../../lib/reporting'
import {
  MAP_RENDER_REPORT_COOLDOWN_MS,
  shouldReportMapRender,
  shouldSampleMapRender,
} from '../../lib/mapPerformance'



export interface MapViewProps {
  state: GameState
  /** Slot number of the viewer (used to highlight "your" territories with a brighter inset). */
  viewerSlot: number
  selectedTerritoryId?: TerritoryId
  validTargetIds?: TerritoryId[]
  onTerritoryPress?: (territoryId: TerritoryId) => void
  /** Tap on the map background (ocean / any non-territory area). Used by the map
   *  explorer + in-game regions view to clear the current selection. */
  onBackgroundPress?: () => void
  /** When every territory is clickable (e.g. the map explorer's tap-to-inspect),
   *  set this so the web pointer cursor shows on all of them, not just
   *  `validTargetIds`. */
  allTerritoriesInteractive?: boolean
  /**
   * Per-territory army-count overrides. When a key is present, the badge
   * shows the override instead of `territory.armies`. Used by the reinforce
   * flow to show "armies + pending placements" before commit, so the player
   * can see the post-commit count without waiting for the API roundtrip.
   */
  armyOverrides?: Record<TerritoryId, number>
  /** Reserved for future pattern-overlay rendering. Currently unused. */
  colorBlindMode?: boolean
  /**
   * Hide the troop-count badges. Useful for the Map Details preview
   * where the badges add no information (territories are unowned and
   * have 0 armies) and only crowd the visual.
   */
  hideBadges?: boolean
  /**
   * Map<regionId, color> — when set, draws a region outline (the same
   * color but thicker stroke) around the union of that region's
   * territories so the Map Details screen can show region groupings at
   * a glance. The territories themselves still render in the player's
   * color (or unowned). Click handling is unchanged.
   */
  highlightRegions?: Record<string, string>
  /**
   * When set, this region's territories render filled with the given
   * highlight color. Used by the Map Details screen to make a tapped
   * region pop visually.
   */
  selectedRegionId?: string
  selectedRegionColor?: string
  /**
   * Territories to highlight as "borders of the currently-selected
   * territory" — used by Map Details when the user taps a single
   * territory so they can see which territories share an edge / sea
   * route with it. Rendered with a contrasting cyan fill that's
   * deliberately distinct from both region-highlight colors and the
   * yellow selection stroke.
   */
  adjacentTerritoryIds?: TerritoryId[]
  /**
   * Territories the CURRENTLY-SELECTED source could act on this phase — the
   * enemies an attack source can hit, or the destinations a fortify source can
   * reach. Drawn as a one-time white outline entrance around each open target
   * and held as a static white glow after the destination is tapped. This is distinct
   * from `validTargetIds`, which controls clickability.
   *
   * Distinct from `validTargetIds`, which is the phase's clickability set (it
   * also covers the pre-selection state where every legal SOURCE is listed) and
   * only drives the web hover cursor. Pass this ONLY while a source is picked
   * and its destination is still open — see lib/turnMapSelection.
   */
  previewTargetIds?: TerritoryId[]
  /**
   * Skip the halo fade-in and show them at full strength immediately. Set by
   * the snapshot/log view, which jumps the whole board to a frozen moment — a
   * fade there trails the change it is supposed to announce.
   */
  previewTargetsInstant?: boolean
  /**
   * How the selected territory's fill renders.
   *   - 'amber' (default): solid amber/gold, used by the inspector
   *     screen where preserving the owner colour doesn't matter.
   *   - 'lighten' (legacy name): preserve the owner fill and mark selection
   *     with inner glows during attack/fortify.
   */
  selectedFillMode?: 'amber' | 'lighten'
  /**
   * Secondary selection — used by Fortify to highlight the destination
   * territory alongside the source. Preserves the owner fill and uses a white
   * inner glow. Ignored when selectedFillMode is 'amber'.
   */
  secondaryTerritoryId?: TerritoryId
  /**
   * Optional overlay pinned to the right edge of the map container,
   * vertically centred on the map's own height. Used to host the
   * player-roster strip so it tracks the middle of the map rather than
   * the middle of the screen — during a turn the HUD takes ~30-40 % of
   * the viewport, so the screen midpoint falls inside the HUD area.
   * The overlay is `pointerEvents: 'box-none'` so map territories
   * underneath stay tappable in any gap; the overlay's own children
   * receive touches normally.
   */
  rightOverlay?: React.ReactNode
  /**
   * Optional overlay anchored to one corner of the RENDERED SVG (not
   * the container — the SVG can letterbox inside its container in
   * landscape). Used for floating chips that must sit ON the map area
   * in every orientation, like the regions-view toggle. Caller picks
   * the corner; MapView positions the node relative to the SVG's
   * actual bounding box so the chip never floats off into screen
   * gutters or HUD space.
   */
  regionToggleNode?: React.ReactNode
  regionToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /** Optional zoom toggle anchored to a corner of the rendered SVG. */
  zoomToggleNode?: React.ReactNode
  zoomToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /**
   * Game event log from `state.events`. When provided, the map watches
   * for new `conquest` events (those with a `ts` greater than any seen
   * before mount) and animates a brief border flash in the new owner's
   * player colour. Historical events (present on mount) are not
   * replayed. Omit when rendering the map outside an active game
   * session (e.g. Map Details preview) to skip the animation machinery.
   */
  events?: GameEvent[]
  /**
   * Pixels already occupied by a sidebar to the LEFT of the map (e.g.
   * the info panel on the Map Details screen in landscape). Forwarded to
   * `useMapDimensions` so the internal aspect-fit calculation uses the
   * narrowed available width rather than the full screen width, preventing
   * the map from over-sizing and clipping under the panel.
   */
  reservedLeft?: number
  /**
   * Pixels reserved at the TOP of the screen (e.g. a header + a toggle
   * row above the map). Forwarded to useMapDimensions so the desktop
   * height policy subtracts it when computing svgHeight, preventing
   * the map from rendering taller than its column and clipping at the
   * bottom under an `overflow-hidden` parent.
   */
  /** Portrait game map: trim the blank authored margins above and below the
   *  territories so the board runs flush between the bars. */
  trimMargins?: boolean
  reservedTop?: number
  /**
   * Pixels reserved at the BOTTOM of the screen (e.g. a fixed-height
   * action panel or activity sidebar below the map). Forwarded to
   * useMapDimensions so the height budget subtracts it before
   * computing svgHeight. Snapshot views in portrait pass this when
   * the bottom-panel sidebar is capped, so the map fits in the
   * remaining pane instead of overflowing the visible area.
   */
  reservedBottom?: number
  /**
   * Pixels reserved at the RIGHT of the screen (e.g. a roster-strip
   * gutter). Forwarded to useMapDimensions so the width budget
   * subtracts it before computing svgWidth. Defaults to
   * DEFAULT_RESERVED_RIGHT_LANDSCAPE in landscape and 0 in portrait.
   * Pass 0 explicitly to suppress the default landscape gutter when
   * the map is already constrained by its flex container (e.g. the
   * game log's split layout where the event list is a sibling column
   * rather than an overlay).
   */
  reservedRight?: number
  /**
   * Landscape notch clearance (defaults true). Pass FALSE when this MapView is
   * already inside a safe-area-padded container so it
   * isn't double-inset. See useMapDimensions.applyNotchInset.
   */
  applyNotchInset?: boolean
  /**
   * When true, render region bonus badges on the map — a filled circle
   * at each region's interior anchor labelled with the bonus number.
   * Rendered ABOVE territory polygons and region overlays but BELOW
   * troop-count badges so troop numbers remain the dominant signal.
   */
  showRegionBonuses?: boolean
  /**
   * When set, the bonus badge for this region renders at 130% radius
   * with a thicker stroke so it "pops" relative to the others. Used
   * when the player has tapped a row in RegionDetailsPanel.
   */
  regionFocusId?: string | null

  // ── Zoom-and-pan ────────────────────────────────────────────────────────
  /**
   * When true, the SVG content group is scaled by ZOOM_SCALE and the user
   * can drag to pan. Territory tap targets remain fully functional — react-
   * native-svg applies the transform to rendered geometry so onPress fires
   * on the visually scaled polygons without any inverse-transform math.
   */
  zoomEnabled?: boolean
  /**
   * Current zoom scale applied to the content group when `zoomEnabled`. The
   * caller owns this so it persists (and is remembered) like `panOffset`.
   * Defaults to DEFAULT_ZOOM_SCALE. Provide `onZoomScaleChange` to surface the
   * on-map +/- zoom buttons.
   */
  zoomScale?: number
  /**
   * Called with a new (clamped) zoom scale when the user taps the on-map +/-
   * buttons. When omitted the buttons are hidden. MapView also emits a
   * recentred `onPanChange` alongside each change so the view zooms about its
   * centre.
   */
  onZoomScaleChange?: (scale: number) => void
  /**
   * Suppress the built-in on-map +/- zoom buttons. Used when an external
   * control bar (MapControlsBar, beneath the map) owns zoom instead, so the
   * steppers aren't duplicated. Pan/pinch behaviour is unaffected.
   */
  hideOnMapZoomButtons?: boolean
  /**
   * Current pan offset in viewBox units. Caller is responsible for hoisting
   * this state so zoom persists across phase changes within a turn. Use
   * `initialPanForFocus` from `lib/mapZoom` to compute the starting value
   * when zoom is first enabled.
   */
  panOffset?: PanOffset
  /**
   * Called with a new PanOffset when the user drags the map. The value is
   * already clamped; the caller should store it directly.
   */
  onPanChange?: (offset: PanOffset) => void
  /**
   * Epoch-ms when the viewer's WebSocket session connected for this game
   * session. Forwarded to `useAttackEvents` so events that arrived between
   * the viewer leaving the screen and returning (e.g. bot attacks) animate
   * on re-mount instead of being silently skipped. When omitted, falls back
   * to the old "skip all history on mount" behaviour.
   */
  viewerJoinedAt?: number
  /**
   * Frozen attack badge for the time-machine snapshot view. When set,
   * an additional `−N, −M` pair is rendered on the from/to territories
   * at full opacity — no animation, no burst, no fade. Independent of
   * the live `events`-driven animation: the live layer can be empty
   * and this badge still draws.
   */
  staticAttackBadge?: import('../../hooks/useAttackEvents').AttackAnimationEntry
  /**
   * Frozen conquest ring for the time-machine snapshot view. When
   * set, the conquered territory gets a non-animating player-colour
   * halo + ring layered on top of the polygon — the same chrome as
   * the live ConquestFlashLayer animation, held at full opacity.
   */
  staticConquestRing?: import('../../hooks/useConquestEvents').ConquestAnimationEntry
  /**
   * Frozen fortify trail for the time-machine snapshot view. When
   * set, a non-animating teal trail + arrowhead is
   * rendered between the from/to territories. Independent of the
   * live fortify-events stream.
   */
  staticFortifyEntry?: import('../../hooks/useFortifyEvents').FortifyAnimationEntry
}

// ─── Theme-aware color palette ────────────────────────────────────────────
// The "ocean" is the background behind the continents — in the Battle Carp
// theme it reads as paper (the bunker / field-manual surface, the same fill
// as the top bar in both themes), not a literal sea. CONTINENT_FILL stays brass-ish (parchment / map fabric),
// borders + badge use olive + ink so the troop counts read on either mode.
// Badge fill/text/stroke palette fields were removed when troop badges
// switched to text-only with WCAG-derived contrast (see ./badgeHelpers).
// Keep ocean/continent/unowned/border so the polygon layer still themes
// correctly across light/dark.
interface MapPalette {
  ocean: string
  continent: string
  unowned: string
  border: string
}

const LIGHT_MAP_PALETTE: MapPalette = {
  ocean: '#f4ecd3', // = paper (light), matching the top bar — dark already matches its paper
  continent: '#b8893a',
  // Resigned/unresolved territories. Cool neutral grey at high opacity so
  // the brass continent underneath doesn't tint it yellow — reads as
  // "abandoned, no side owns this" rather than "desaturated brass."
  unowned: 'rgba(150, 155, 160, 0.88)',
  border: '#4a5a2a',           // olive (light)
}

const DARK_MAP_PALETTE: MapPalette = {
  ocean: '#2c3520',
  continent: '#d9a85a',
  unowned: 'rgba(150, 155, 162, 0.85)',
  border: '#8aa14a',           // olive (dark)
}

const SELECTED_STROKE = '#fbbf24'      // amber-400 for selection (cross-theme)
const SELECTED_FILL = '#fbbf24'        // amber-400 fill for the selected territory itself
// Territory fill opacity on a backdrop (raster-art) map. The base value keeps
// ownership tints translucent so the artwork stays legible underneath; the
// emphasis value is what a SELECTED territory or focused region gets, so
// selection illuminates on a backdrop map the way it does on a vector one.
// Resting opacity during live play: the artwork is the board, so ownership
// tints stay translucent over it.
const BACKDROP_FILL_OPACITY = 0.55
// Regions view is a different job. There the tint IS the information, and a
// 0.55 wash died on exactly the cells that matter most — forts and batteries
// sit under tall dark 3D structures, so the near-black art underneath dragged
// their colour down to a grey smudge while flat ground cells looked fine. Every
// region-owned cell gets a near-solid tint there instead.
const BACKDROP_REGION_FILL_OPACITY = 0.85
// The selected / focused region on top of that.
const BACKDROP_EMPHASIS_FILL_OPACITY = 0.92
// How much white to mix into a region colour before painting it on raster art.
// Vector maps use 0.55 (nothing underneath competes, so the tint can be pale);
// keeping that here crushed every pin to 5–39% saturation and the grey art
// finished the job. Low value = the author's colour survives.
const BACKDROP_REGION_TINT_LIGHTEN = 0.18
// Opacity of the structure re-light drawn OVER the walls raster for the selected
// region (see `structureRelightLayer`). Must stay below 1 — the remaining
// (1 − this) is what lets the building's contours show through, and it is the
// whole difference between a lit fort and a flat coloured silhouette.
//
// Calibrated on the four Capture the Flag keeps. Their pixels come in two classes:
// roughly half take the territory fill (already the lit colour C), and half are
// opaque near-black line-art K. This layer leaves the first class at C and moves
// the second to `α·C + (1−α)·K`, so α IS the contrast between stonework and
// roofline:
//
//   α = 0.72  building reaches the chip colour but reads as a flat cut-out
//             (contours at 28% weight — measured, rejected)
//   α = 0.62  bright, with the contour pass below restoring the black line-art
//   α = 0.40  contours dominate; the fort starts going back to a dark lump
//
// Brightness is only half the job: α lifts the near-black OUTLINES along with
// everything else, which is what washed the stonework out. See
// STRUCTURE_CONTOUR_OPACITY.
const STRUCTURE_RELIGHT_OPACITY = 0.62
// The walls raster drawn a SECOND time over the re-lit region, translucent, to put
// the building's dark outlines back on top of the wash.
//
// It works because the overlay is transparent everywhere it isn't art: the second
// draw only touches walls, structures and line-art. Compositing a pixel of art
// value V over the re-light (α·C + (1−α)·V) gives β·V + (1−β)·(α·C + (1−α)·V), so
// how much a pixel darkens scales with how dark it already was. A black outline
// (V≈0) lands at (1−β)·α·C while the mid-grey stone faces around it barely move —
// the outlines come back as outlines instead of the whole structure going grey.
//
// At 0 the fort is a flat coloured cut-out (what the maintainer saw); at 1 the art
// is fully restored and the re-light is undone. Clipped to the region shape, so it
// only ever touches the selected region's own footprint.
const STRUCTURE_CONTOUR_OPACITY = 0.4
// Default per-region stroke palette — imported from regionColors.ts.
// Applied when the caller doesn't supply `highlightRegions` (i.e. in-game
// view). Same hues the inspector uses for region outlines so a player's
// mental model of region colours stays consistent across screens.
// See regionColors.ts for CVD / player-color separation rationale.

// Fill applied to neighbours of the inspect-screen selected territory.
// Cyan reads against both the brass-tan continent fill (light theme) and
// the dark olive ocean (dark theme), and is distinctly NOT a region or
// player color — keeps the "this is showing borders" signal unambiguous.
// Sea-route lines connecting non-contiguous adjacent territories (Alaska↔
// Kamchatka, Brazil↔North Africa, etc). Solid connectors sit behind the troop
// badges so badges remain the dominant visual.
const SEA_ROUTE_STROKE = MAP_SYMBOL_COLORS.route     // sky-500 (shared with the map key)

// Raster-backed maps already carry their region artwork, but the live
// territory shapes still need a readable stroke over that artwork. These two
// Old World pins deliberately use a muted/lifted line colour: the Porte's
// source colour is very dark, while Iberia's source line was too hot red.
const BACKDROP_TERRITORY_STROKE_OVERRIDES: Record<string, string> = {
  balkans: '#A95180',
  spain: '#B85C5C',
}

// ── Native tap slop (see the slop layer at the bottom of the SVG) ────────────
// A territory whose rendered footprint is narrower than this (√area, in screen
// px) is treated as "small" and gets a widened hit area. 44 is the
// iOS HIG minimum tap target, which is exactly the line being crossed: below it
// a finger cannot reliably land inside the polygon.
const TAP_SLOP_SMALL_PX = 44
// How far outward (screen px) a small territory's hit area extends past its
// own outline. Deliberately small — every pixel of it is stolen from whatever
// neighbour it overlaps, so this buys reliability on the cells that need it
// without making the big ones feel loose.
const TAP_SLOP_PX = 6

function ownerFill(
  ownerId: string,
  slotByPlayerId: Record<string, number>,
  unowned: string,
): string {
  if (!ownerId) return unowned
  const slot = slotByPlayerId[ownerId]
  if (!slot) return unowned
  // Use the player's color but with some transparency so borders + labels read.
  return PLAYER_COLORS[clampPlayerSlot(slot)].hex
}


// The zoom scale applied to the content group is now caller-driven via the
// `zoomScale` prop (defaulting to DEFAULT_ZOOM_SCALE from lib/mapZoom), so the
// on-map +/- buttons can step it between MIN_ZOOM_SCALE and MAX_ZOOM_SCALE.

/**
 * Round +/- control rendered at the bottom-centre of the map in zoom mode.
 * Styled to match the corner zoom/region chips (paper fill, olive border).
 */
function ZoomButton({
  label,
  accessibilityLabel,
  disabled,
  onPress,
}: {
  label: string
  accessibilityLabel: string
  disabled: boolean
  onPress: () => void
}) {
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      className="bg-paper border-2 border-olive rounded-full items-center justify-center"
      style={{ width: 44, height: 44, opacity: disabled ? 0.4 : 1 }}
    >
      <Text className="text-ink" style={{ fontSize: 24, lineHeight: 28 }}>
        {label}
      </Text>
    </TouchableOpacity>
  )
}

// ─── Module-level memoisation for assignRegionColors ─────────────────────────
// assignRegionColors is deterministic given (geometryVersion, playerCount) because
// map region definitions are static and the greedy graph-coloring pass is
// seeded only from region adjacency structure + region.color pins (both
// constant per map). Caching at module level avoids re-running the O(R²)
// ΔE76 scoring on every MapView render — only the first render per map pays
// the cost.
//
// Key: `${geometryVersion}-${regionCount}` — regionCount instead of playerCount because
// the output depends only on the region graph (adjacencies + color pins), not
// on which players are in the game. geometryVersion differentiates map
// revisions, including revisions that keep the same map id and counts.
const _regionColorCache = new Map<string, Record<RegionId, string>>()

/**
 * Return the cached region-color assignment for a map, computing it on first
 * access.  `adjacencyGraph` must be freshly computed from the same territories
 * / regions arrays (it is never cached here — the outer useMemo handles that).
 */
function getCachedRegionColors(
  geometryVersion: string,
  regions: ReadonlyArray<{ id: string; color?: string }>,
  adjacencyGraph: ReadonlyMap<string, ReadonlySet<string>>,
): Record<RegionId, string> {
  const key = `${geometryVersion}-${regions.length}`
  const cached = _regionColorCache.get(key)
  if (cached) return cached
  const result = assignRegionColors(regions, adjacencyGraph)
  _regionColorCache.set(key, result)
  return result
}

// ─── Module-level memoisation for static per-map geometry ────────────────────
// Sea-route connectors, pole-of-inaccessibility badge positions, polygon areas,
// and region-bonus anchors are all derived PURELY from the map's static
// geometry (territory / region shapes, adjacencies, viewBox). Only `ownerId`
// and `armies` change turn-to-turn, and none of these computations read them.
// But `state.map.territories` is a fresh array reference on every game-state
// push, so the in-component useMemos keyed off it recomputed all of this on
// every render — and again on every phase remount (each turn phase renders its
// own MapView). The sea-route pass in particular is O(T²) (every candidate line
// is tested against every other polygon via lineCrossesPolygon).
//
// Cache at module level keyed by geometryVersion + territoryCount + suppressKey + mapX + mapW.
// The version guards against a map update changing geometry while keeping the
// same map id and counts. mapX and mapW MUST be part of the key: in landscape mode
// computeMapDimensions pads the viewBox proportionally based on screen width
// and the caller's reservedLeft/reservedRight context, so mapX and mapW differ
// across render contexts even for the same map. The wraparound-stub geometry
// anchors endpoints at mapX and mapX+mapW; a stale cached result computed at
// wrong dimensions would place those stubs at the wrong screen positions,
// producing the "thick/fuzzy/misplaced" first-render artefact. See
// buildSeaRouteCacheKey in seaRouteHelpers.ts for the rounding rationale.

type MapTerritory = GameState['map']['territories'][number]
type MapRegion = GameState['map']['regions'][number]
type WraparoundAdjacencies = GameState['map']['wraparoundAdjacencies']
type SuppressSeaRoutes = GameState['map']['suppressSeaRoutes']
type ForceSeaRoutes = GameState['map']['forceSeaRoutes']
type ForceSeaRouteTuning = GameState['map']['forceSeaRouteTuning']
type Anchor = { x: number; y: number }


const _seaRouteCache = new Map<string, SeaRoute[]>()
const _badgePositionCache = new Map<
  string,
  ReturnType<typeof computeAlignedBadgePositions>
>()
const _areaCache = new Map<string, Record<string, number>>()
const _regionBonusAnchorCache = new Map<string, Record<string, Anchor>>()


function getCachedSeaRoutes(
  geometryVersion: string,
  territories: ReadonlyArray<MapTerritory>,
  wraparoundAdjacencies: WraparoundAdjacencies,
  suppressSeaRoutes: SuppressSeaRoutes,
  forceSeaRoutes: ForceSeaRoutes,
  forceSeaRouteTuning: ForceSeaRouteTuning,
  mapX: number,
  mapW: number,
  precomputedSeaRoutes?: SeaRoute[],
): SeaRoute[] {
  // Include the suppress list so cached results don't leak across maps that
  // have different suppression sets. The list is static per map, so a JSON
  // of it is stable and cheap to compute.
  // mapX and mapW are included because the wraparound-stub endpoints anchor
  // to the viewBox edges (mapX, mapX+mapW) — see buildSeaRouteCacheKey.
  // Both override lists go in the key: two maps with the same id and territory
  // count but different overrides must not share a cached result.
  const suppressKey =
    (suppressSeaRoutes ? JSON.stringify(suppressSeaRoutes) : '') +
    (forceSeaRoutes ? `!${JSON.stringify(forceSeaRoutes)}` : '') +
    (forceSeaRouteTuning ? `~${JSON.stringify(forceSeaRouteTuning)}` : '')
  const key = buildSeaRouteCacheKey(geometryVersion, territories.length, suppressKey, mapX, mapW)
  const hit = _seaRouteCache.get(key)
  if (hit) return hit
  if (precomputedSeaRoutes) {
    _seaRouteCache.set(key, precomputedSeaRoutes)
    return precomputedSeaRoutes
  }
  const result = computeSeaRoutes(
    territories,
    wraparoundAdjacencies,
    suppressSeaRoutes,
    forceSeaRoutes,
    mapX,
    mapW,
    forceSeaRouteTuning,
  )
  _seaRouteCache.set(key, result)
  return result
}

function getCachedBadgePositions(
  geometryVersion: string,
  territories: Parameters<typeof computeAlignedBadgePositions>[0],
): ReturnType<typeof computeAlignedBadgePositions> {
  const key = `${geometryVersion}-${territories.length}`
  const hit = _badgePositionCache.get(key)
  if (hit) return hit
  const result = computeAlignedBadgePositions(territories)
  _badgePositionCache.set(key, result)
  return result
}

/** Polygon area per territory id — used only to scale the troop-badge font. */
function getCachedAreas(
  geometryVersion: string,
  territories: ReadonlyArray<MapTerritory>,
): Record<string, number> {
  const key = `${geometryVersion}-${territories.length}`
  const hit = _areaCache.get(key)
  if (hit) return hit
  const result: Record<string, number> = {}
  for (const t of territories) result[t.id] = polygonAreaFromD(t.shape)
  _areaCache.set(key, result)
  return result
}

/**
 * Region-bonus badge anchors — the pole-of-inaccessibility of each region's
 * union shape (or the average of its territory centroids as a fallback).
 * Purely geometric (region shapes + territory positions are static per map),
 * so cache by `${geometryVersion}-${regionCount}` like the other geometry helpers.
 */
function getCachedRegionBonusAnchors(
  geometryVersion: string,
  regions: ReadonlyArray<MapRegion>,
  territories: ReadonlyArray<MapTerritory>,
): Record<string, Anchor> {
  const key = `${geometryVersion}-${regions.length}`
  const hit = _regionBonusAnchorCache.get(key)
  if (hit) return hit
  const posById = new Map<string, Anchor>()
  for (const t of territories) posById.set(t.id, t.position)
  const anchors: Record<string, Anchor> = {}
  for (const region of regions) {
    if (region.bonusAnchor) {
      anchors[region.id] = region.bonusAnchor
      continue
    }
    const positions = region.territoryIds
      .map((tid) => posById.get(tid))
      .filter((p): p is Anchor => p !== undefined)
    anchors[region.id] = regionBadgeAnchor(region.shape, positions)
  }
  _regionBonusAnchorCache.set(key, anchors)
  return anchors
}

// A legacy/custom MapDef may not carry the generated version yet. Cache the
// fallback by its immutable territories array so a live-state update does not
// hash the entire map on every render.
const _fallbackGeometryVersionCache = new WeakMap<ReadonlyArray<MapTerritory>, string>()

function getMapGeometryVersion(map: GameState['map']): string {
  if (map.geometryVersion) return map.geometryVersion
  const hit = _fallbackGeometryVersionCache.get(map.territories)
  if (hit) return hit
  const version = mapGeometryVersion(map)
  _fallbackGeometryVersionCache.set(map.territories, version)
  return version
}

/**
 * Return route geometry baked into the map bundle when it is safe to reuse.
 * Wraparound routes are reusable in portrait, where the viewBox is the native
 * map viewBox. Landscape pads the viewBox, so its edge stubs are recomputed
 * against that viewport.
 */
function getPrecomputedSeaRoutes(
  mapId: string,
  seaRoutes: GameState['map']['seaRoutes'],
  wraparoundAdjacencies: WraparoundAdjacencies,
  geometryVersion: string,
  mapX: number,
  mapW: number,
  sourceViewBox: GameState['map']['viewBox'],
): SeaRoute[] | undefined {
  if (
    wraparoundAdjacencies?.length &&
    !canUseBaseViewBoxSeaRoutes(mapX, mapW, sourceViewBox)
  ) {
    return undefined
  }
  if (seaRoutes) return seaRoutes
  const bundled = getBundledMap(mapId)
  if (bundled?.geometryVersion === geometryVersion && bundled.seaRoutes) {
    return bundled.seaRoutes
  }
  return undefined
}

/**
 * TerritoryShape — one territory's polygon (or dot-fallback) as its own
 * `React.memo`'d node so a change to ONE territory (selection, owner, armies,
 * an animation) reconciles only that territory instead of re-rendering every
 * polygon on the map. All visual state is derived by the parent and handed in
 * as primitive props; `onPress` is a stable callback (see handleTerritoryPress)
 * so the default shallow prop comparison short-circuits unchanged territories.
 */
interface TerritoryShapeProps {
  territoryId: string
  /** Per-MapView-instance clip-id namespace (see clipNS in MapViewInner). */
  clipNS: string
  shape?: string
  positionX: number
  positionY: number
  circleRadius: number
  fill: string
  /** <1 tints the territory over a backdrop image instead of hiding it. */
  fillOpacity?: number
  /**
   * 'evenodd' when a shape may contain hole subpaths (annular territories that
   * ring a building/tower — see the CTF map). Even-odd fill cuts the inner
   * structure out of the fill AND the clip so the wrapped territory shows and
   * is independently clickable. Omitted → renderer default (nonzero), identical
   * for the simple single-subpath polygons every other map uses.
   */
  fillRule?: 'evenodd'
  stroke: string
  strokeWidth: number
  /** False for the paint-only selection overlays, which must not duplicate a11y targets. */
  interactive?: boolean
  accessibilityLabel: string
  onPress?: (territoryId: string) => void
  /** Web-only: show a pointer cursor on hover (territory is actionable). */
  pointer?: boolean
}

const TerritoryShape = React.memo(function TerritoryShape({
  territoryId,
  clipNS,
  shape,
  positionX,
  positionY,
  circleRadius,
  fill,
  fillOpacity,
  fillRule,
  stroke,
  strokeWidth,
  interactive = true,
  accessibilityLabel,
  onPress,
  pointer,
}: TerritoryShapeProps) {
  return (
    <G
      testID={interactive ? `territory-${territoryId}` : undefined}
      onPress={interactive && onPress ? () => onPress(territoryId) : undefined}
      accessibilityLabel={interactive ? accessibilityLabel : undefined}
      pointerEvents={interactive ? undefined : 'none'}
      // The fill rule has to be on the GROUP too, not just the Path, or an
      // annular territory swallows every tap aimed at the territory sitting in
      // its hole (Capture the Flag's building bases — untappable on iOS,
      // fine on web).
      //
      // Why: react-native-svg's iOS hit test asks the Path first, which honours
      // even-odd and correctly reports "not me" over the hole. The group then
      // falls through to its OWN hit test, whose hit area is the union of its
      // children's paths — tested with the GROUP's fillRule, which defaults to
      // nonzero. Nonzero counts the hole as interior, so the group claims the
      // touch and the enclosed territory never sees it. Web hit-tests by paint
      // order against the even-odd fill, which is why it only broke on device.
      fillRule={fillRule}
      // Pointer cursor on desktop web for actionable territories. cursor is
      // inherited, so setting it on the group applies to the Path/Circle child.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      style={pointer && Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : undefined}
      // Intentionally no accessibilityRole="button" — react-native-web turns a
      // role=button <G> into an <html:button>, which can't legally contain SVG
      // <path> children, and the map renders blank. Keep it a plain SVG group.
    >
      {shape ? (
        <Path
          d={shape}
          fill={fill}
          fillOpacity={fillOpacity}
          fillRule={fillRule}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
          clipPath={`url(#${clipNS}clip-${territoryId})`}
        />
      ) : (
        <Circle
          cx={positionX}
          cy={positionY}
          r={circleRadius}
          fill={fill}
          fillOpacity={fillOpacity}
          stroke={stroke}
          strokeWidth={strokeWidth}
        />
      )}
    </G>
  )
})

interface MapAnimationLayersProps {
  events: GameEvent[] | undefined
  slotByPlayerId: Record<string, number>
  playerColorHex: (slot: number) => string
  viewerJoinedAt?: number
  shapeById: Record<string, string | undefined>
  positionById: Record<string, { x: number; y: number } | undefined>
  /** PoI-corrected badge position for each territory — used by the fortify
   *  trail layer so the arrowhead tip lands on the badge edge. */
  badgePositionById: Record<string, { x: number; y: number } | undefined>
  /** Badge circle radius in viewBox units (Math.max(2.5, sizingWidth * 0.022)). */
  badgeRadius: number
  conquestFlashStrokeWidth: number
  badgeFontSize: number
  /** Font for the attack "−N" badges (smaller lift in landscape). */
  attackBadgeFontSize: number
  fortifyTrailWidth: number
  staticConquestRing?: ConquestAnimationEntry
  staticAttackBadge?: AttackAnimationEntry
  staticFortifyEntry?: FortifyAnimationEntry
  /** The drawn map: attack loss labels stay inside it. */
  badgeBounds?: { x: number; y: number; width: number; height: number }
}

/**
 * MapAnimationLayers — the live placement/attack/fortify animation layers and
 * the hooks that drive them, split out of MapViewInner so their state churn
 * stays local. Conquest flashes follow the same pattern in
 * ConquestFlashOverlays, beside the overlay <Svg>s rather than inside one.
 *
 * Each hook's `setEntries` fires INDEPENDENTLY of game-state changes — the
 * animation start (deferred via InteractionManager) and the expiry timer that
 * clears the entry ~1.3s later. Keeping the hooks here means those ticks
 * re-render only this memoized sibling, not MapViewInner — which would
 * otherwise re-run the whole board's per-territory fill/stroke derivation on
 * every animation frame, especially during bot-turn conquest→attack→fortify
 * cascades. Props are referentially stable across a parent re-render (memoized
 * lookups, primitive sizes, a stable playerColorHex), so a selection change in
 * MapViewInner doesn't needlessly re-run these hooks either.
 */
/**
 * One territory's troop count, memoised on PRIMITIVES.
 *
 * The badge layer used to be an inline pass over every territory, so one army
 * moving re-created all of them — 44 <G> groups and 88 <SvgText> nodes on Arctic,
 * 316 on Forbidden City — and react-native-svg re-applied props to every one on
 * the UI thread. This is the same trick `TerritoryShape` uses: hand it flat
 * values and React skips the ones that did not change, so a conquest touches two
 * badges instead of all of them.
 *
 * Two <SvgText> nodes because react-native-svg's TextProps does not expose
 * `paintOrder`, so the "stroke behind fill" outline is emulated by drawing a
 * thicker stroked copy first and the solid fill on top.
 * Web uses a real ExtraBold (heavyNumberFont).
 */
const TroopBadge = React.memo(function TroopBadge({
  x,
  y,
  label,
  fontSize,
  fill,
  outline,
  outlineWidth,
  opacity,
}: {
  x: number
  y: number
  label: string
  fontSize: number
  fill: string
  outline: string
  outlineWidth: number
  opacity: number
}) {
  const baselineY = y + fontSize * 0.36
  return (
    <G pointerEvents="none" opacity={opacity}>
      <SvgText
        x={x}
        y={baselineY}
        fill={outline}
        stroke={outline}
        strokeWidth={outlineWidth}
        strokeLinejoin="round"
        fontSize={fontSize}
        fontFamily={heavyNumberFont()}
        fontWeight="800"
        textAnchor="middle"
      >
        {label}
      </SvgText>
      <SvgText
        x={x}
        y={baselineY}
        fill={fill}
        fontSize={fontSize}
        fontFamily={heavyNumberFont()}
        fontWeight="800"
        textAnchor="middle"
      >
        {label}
      </SvgText>
    </G>
  )
})

const MapAnimationLayers = React.memo(function MapAnimationLayers({
  events,
  slotByPlayerId,
  playerColorHex,
  viewerJoinedAt,
  shapeById,
  positionById,
  badgePositionById,
  badgeRadius,
  conquestFlashStrokeWidth,
  badgeFontSize,
  attackBadgeFontSize,
  fortifyTrailWidth,
  staticConquestRing,
  staticAttackBadge,
  staticFortifyEntry,
  badgeBounds,
}: MapAnimationLayersProps) {
  const placementEntries = usePlacementEvents(events, slotByPlayerId, playerColorHex, viewerJoinedAt)
  const attackEntries = useAttackEvents(events, slotByPlayerId, playerColorHex, viewerJoinedAt)
  const fortifyEntries = useFortifyEvents(events, slotByPlayerId, playerColorHex, viewerJoinedAt)

  return (
    <>
      {/* The time-machine snapshot's frozen conquest ring. Live conquest
          flashes are ConquestFlashOverlays, outside this <Svg>. */}
      <ConquestRingLayer
        entry={staticConquestRing}
        shapeById={shapeById}
        strokeWidth={conquestFlashStrokeWidth}
      />
      {/* Placement "+N" badges on reinforce events. */}
      <PlacementBadgeLayer
        entries={placementEntries}
        positionById={positionById}
        badgeFontSize={badgeFontSize}
      />
      {/* The attack's red damage pulses. Its "−N" labels are AttackLabelLayers,
          in their own overlay above the troop numbers. */}
      {/* Red from→to arrow for the snapshot's frozen attack, so a log row
          shows which way the attack went, not just the two territories.
          Mounted BEFORE the badge layer: SVG paints in source order, so the
          "−N, −M" losses (a higher overlay) always sit on top of the shaft.
          Snapshot-only — it keys off staticEntry and never sees live attacks. */}
      <AttackArrowLayer
        staticEntry={staticAttackBadge}
        positionById={positionById}
        badgePositionById={badgePositionById}
        badgeRadius={badgeRadius}
        trailWidth={fortifyTrailWidth}
      />
      <AttackBadgeLayer
        part="pulses"
        entries={attackEntries}
        positionById={positionById}
        badgeFontSize={attackBadgeFontSize}
        shapeById={shapeById}
      />
      {/* Fortify trail + arrowhead. staticEntry forwards the
          snapshot's frozen copy for fortify log rows.
          badgePositionById gives the PoI-corrected destination badge
          centre; badgeRadius is the circle's radius — together they let
          the arrowhead tip land exactly on the badge circumference. */}
      <FortifyTrailLayer
        entries={fortifyEntries}
        positionById={positionById}
        badgePositionById={badgePositionById}
        badgeRadius={badgeRadius}
        trailWidth={fortifyTrailWidth}
        staticEntry={staticFortifyEntry}
      />
    </>
  )
})

/**
 * The attack "−N" loss labels, in their own overlay ABOVE the troop numbers on
 * every map: drawn under them, a neighbouring territory's count could cover a
 * label wherever territories sit close together. Its own memoized sibling for
 * the same reason as MapAnimationLayers; the attack hook is pure derived state,
 * so this copy and that one's (for the pulses) see the same entries.
 * staticEntry forwards the time-machine snapshot's frozen badge pair.
 */
const AttackLabelLayers = React.memo(function AttackLabelLayers({
  events,
  slotByPlayerId,
  playerColorHex,
  viewerJoinedAt,
  positionById,
  attackBadgeFontSize,
  staticAttackBadge,
  badgeBounds,
}: Pick<MapAnimationLayersProps, 'events' | 'slotByPlayerId' | 'playerColorHex' | 'viewerJoinedAt'
  | 'positionById' | 'attackBadgeFontSize' | 'staticAttackBadge' | 'badgeBounds'>) {
  const attackEntries = useAttackEvents(events, slotByPlayerId, playerColorHex, viewerJoinedAt)
  return (
    <AttackBadgeLayer
      part="labels"
      entries={attackEntries}
      positionById={positionById}
      badgeFontSize={attackBadgeFontSize}
      staticEntry={staticAttackBadge}
      bounds={badgeBounds}
    />
  )
})

const trimOpts = (trim: { top: number; bottom: number }) => ({ trimTop: trim.top, trimBottom: trim.bottom })

function MapViewInner({
  state,
  viewerSlot: _viewerSlot,
  selectedTerritoryId,
  validTargetIds = [],
  allTerritoriesInteractive = false,
  onTerritoryPress,
  onBackgroundPress,
  armyOverrides,
  hideBadges = false,
  highlightRegions,
  selectedRegionId,
  selectedRegionColor,
  adjacentTerritoryIds = [],
  previewTargetIds = [],
  previewTargetsInstant = false,
  selectedFillMode = 'amber',
  secondaryTerritoryId,
  rightOverlay,
  regionToggleNode,
  regionToggleCorner = 'bottom-right',
  zoomToggleNode,
  zoomToggleCorner = 'bottom-right',
  events,
  reservedLeft,
  reservedTop,
  reservedBottom,
  reservedRight,
  applyNotchInset,
  trimMargins = false,
  showRegionBonuses = false,
  regionFocusId,
  zoomEnabled = false,
  zoomScale,
  onZoomScaleChange,
  hideOnMapZoomButtons = false,
  panOffset,
  onPanChange,
  viewerJoinedAt,
  staticAttackBadge,
  staticConquestRing,
  staticFortifyEntry,
}: MapViewProps) {
  const { theme } = useTheme()
  // A map can bring its own backdrop colour per theme (MapDef.backdropColor).
  const basePalette = theme === 'dark' ? DARK_MAP_PALETTE : LIGHT_MAP_PALETTE
  const backdropColor = state.map.backdropColor?.[theme === 'dark' ? 'dark' : 'light']
  const palette = React.useMemo(() => (backdropColor ? { ...basePalette, ocean: backdropColor } : basePalette), [basePalette, backdropColor])
  // The regions-view spotlight wash. A vivid backdrop (Europe Classic's sea)
  // must not tint the unlit board, so it uses the theme's neutral canvas.
  const spotlightDim = resolveSpotlightDimColor(palette.ocean, basePalette.ocean)
  // insets.right is threaded into mapDimensions so the map's right
  // edge stops at the iPhone Dynamic Island on landscape-right
  // rotation. insets.left is NOT factored in here — the action-panel
  // header pads itself at the component level (so its cog + roster
  // stay visible when the notch is on screen-left). NATIVE only —
  // iOS Safari handles notch via viewport-fit and the existing web
  // layout was already correct.
  // Landscape notch clearance is handled inside useMapDimensions — it shrinks
  // svgWidth/containerWidth by the horizontal safe insets so the map (and the
  // controls bar, which sizes to the same values) never render under the notch.
  const { svgWidth, svgHeight, containerWidth, viewBox, sizingWidth, isLandscape } = useMapDimensions(state, {
    reservedLeft,
    reservedTop,
    reservedBottom,
    reservedRight,
    applyNotchInset,
    ...(trimMargins ? trimOpts(mapVerticalTrim(state.map)) : null),
  })
  const mapW = viewBox.width
  const mapH = viewBox.height
  const mapX = viewBox.x ?? 0
  const mapY = viewBox.y ?? 0
  const aspect = mapW / mapH
  // The raster art layers (backgroundImage / wallsOverlay) must be pinned to the
  // map's NATIVE viewBox, never the one useMapDimensions returns.
  //
  // In landscape that returned viewBox is PADDED — widened (or heightened) so a
  // wide window shows empty space around a centred map. Territory polygons keep
  // their native coordinates and simply sit centred inside the bigger box, but
  // these images are drawn to an explicit rect with preserveAspectRatio="none",
  // so handing them the padded box stretched the art across the padding while
  // the polygons stayed put. The result was art sliding out from under its own
  // territories, and only past the landscape breakpoint — portrait returns the
  // source viewBox unchanged, which is why a phone looked perfect.
  const nativeViewBox = state.map.viewBox
  const artX = nativeViewBox.x ?? 0
  const artY = nativeViewBox.y ?? 0
  const artW = nativeViewBox.width
  const artH = nativeViewBox.height
  // Element sizes are expressed in viewBox units. To get a consistent
  // rendered pixel size across maps with very different viewBox widths
  // (SA=159, Carpathia=1600), scale by `sizingWidth` — the map's NATIVE
  // width. Do NOT use mapW (= viewBox.width) here: in landscape the
  // viewBox is padded horizontally to fill the wrapper, so mapW grows
  // with screen width. Since the polygon scale is pinned by height, that
  // made troop numbers balloon on wide/horizontal screens while the map
  // stayed the same size. sizingWidth keeps every element proportional to
  // the rendered polygons regardless of orientation or window width. A
  // factor of 0.014 makes 14-ish rendered pixels at a 1000-px-wide canvas.
  const labelFontSize = Math.max(2, sizingWidth * 0.014)
  const badgeRadius = Math.max(2.5, sizingWidth * 0.022)
  // Troop numbers read a touch small in landscape (the map is wider on
  // screen but the count is the only per-territory text), so bump the
  // font there. This is a proportional multiplier on the native-width
  // basis — it does NOT reintroduce the width-growth bug, it just makes
  // landscape numbers slightly larger than portrait at every screen size.
  // Desktop (a window 1024+ wide, as the game screen decides) uses 1.125,
  // halfway between portrait and 1.25, which read too big there.
  const { width: windowWidth } = useWindowDimensions()
  const desktop = isLandscape && windowWidth >= 1024
  const badgeFontScale = isLandscape ? (desktop ? 1.125 : 1.25) : 1
  // Attack casualty badges (−x / −y) scale up from the troop font; on desktop
  // they get a smaller lift (1.15 rather than 1.3), as they too read too big.
  const attackBadgeFontSize = Math.max(2.5, sizingWidth * 0.02) * badgeFontScale * (desktop ? 1.15 / 1.3 : 1)
  const badgeFontSize = Math.max(2.5, sizingWidth * 0.02) * badgeFontScale
  // Stroke widths are expressed in viewBox units and scale with
  // sizingWidth (native map width) — same reasoning as the badge/label
  // sizes above: keep the rendered stroke pixels constant across
  // orientations rather than growing with the padded landscape viewBox.
  // Stroke widths — driven by edgeConfig sliders calibrated in the
  // map-editor. The model is the simplest possible: two clipped strokes
  // produce the visible border hierarchy.
  //
  //   1. Each territory polygon is stroked at `internalBorderWidth`,
  //      clipped to its own interior. At a shared internal edge between
  //      two same-region cells the two clipped halves sum to the full
  //      stroke width → visible internal border = internalBorderWidth.
  //
  //   2. Each region.shape (union of its territories) is stroked at
  //      `regionBorderWidth`, clipped to the region's interior. Only the
  //      inner half is visible — adding regionBorderWidth/2 on the
  //      region's side of every outer-perimeter pixel.
  //
  // Net visible thicknesses, with I = internalBorderWidth, R = regionBorderWidth:
  //   - internal same-region edge   : I
  //   - single-region perimeter     : I/2 (cell side) + R/2 (region side)
  //   - cross-region edge (2 regs)  : 2 × (I/2 + R/2) = I + R
  //   - coastal (region edge, ocean): I/2 + R/2 (same as single-region perimeter)
  //
  // For the 1 : 2 : 4 ratio spec the calibration is R = 3I → perimeter = 2I,
  // cross-region = 4I, coastal = 2I.
  const internalBorderWidth = state.map.edgeConfig?.internalBorderWidth ?? Math.max(0.5, sizingWidth * 0.0018) * 2
  const regionBorderWidth = state.map.edgeConfig?.regionBorderWidth ?? internalBorderWidth * 3
  // Raster-backed maps draw a thin internal border over their art, unless the
  // map asks for full-width region-coloured ones (regionColoredInternalBorders).
  const territoryBorderWidth =
    state.map.backgroundImage && !state.map.regionColoredInternalBorders ? internalBorderWidth * 0.35 : internalBorderWidth
  const validStrokeWidth = Math.max(0.8, sizingWidth * 0.003)
  const dashLen = Math.max(1.5, sizingWidth * 0.006)
  // Selected-territory inspector outline width (the `amber` inspector
  // case). Scales off the region border so it stays visually dominant
  // against the bright amber fill.
  const selectedStrokeWidth = Math.max(1, regionBorderWidth * 0.75)

  const adjacentSet = React.useMemo(
    () => new Set(adjacentTerritoryIds),
    [adjacentTerritoryIds],
  )

  /**
   * Territories the SELECTED one borders ONE-WAY. Highlighted like an ordinary
   * neighbour but in violet: they are reachable from the selection and cannot
   * reach back, which is invisible in the art.
   */
  // ── Stable geometry snapshot ──────────────────────────────────────────────
  //
  // Shapes, positions and region outlines never change while a map is open —
  // only `ownerId` and `armies` do. But every game-cache write produces a new
  // `state.map.territories` array (one army moved is enough), so any layer keyed
  // on that array was rebuilt for churn that could not affect it.
  //
  // Pin the arrays per geometry version. The purely GEOMETRIC layers below key
  // off this, so
  // they are built once per map and then skipped entirely — React sees the same
  // element reference and does not reconcile the subtree at all.
  const geometryVersion = getMapGeometryVersion(state.map)
  const geomRef = React.useRef<{
    version: string
    territories: typeof state.map.territories
    regions: typeof state.map.regions
  } | null>(null)
  if (geomRef.current?.version !== geometryVersion) {
    geomRef.current = {
      version: geometryVersion,
      territories: state.map.territories,
      regions: state.map.regions,
    }
  }
  const geomTerritories = geomRef.current.territories
  const geomRegions = geomRef.current.regions
  const territoryShapeById = React.useMemo(
    () => Object.fromEntries(geomTerritories.map((territory) => [territory.id, territory.shape])),
    [geomTerritories],
  )
  const territoryRegionShapeById = React.useMemo(() => {
    const regions = new Map(geomRegions.map((region) => [region.id, region.shape]))
    return Object.fromEntries(geomTerritories.map((territory) =>
      [territory.id, regions.get(territory.regionId ?? '')],
    ))
  }, [geomRegions, geomTerritories])
  // During live attack/fortify selection, the source and its legal
  // destinations are the only territories answering the current question.
  // Keep the dim for the whole source/target selection pair: after a
  // destination is picked, turnMapSelection clears previewTargetIds but keeps
  // selectedTerritoryId + secondaryTerritoryId until the engagement is exited.
  // Historical views use the same tint, keeping every territory touched by
  // the event clear (including multi-territory placements and amber claims).
  // Clearing the selection fades the dim out in either view.
  const selectionDimmedTerritoryIds = React.useMemo(() => {
    const active =
      (selectedFillMode === 'lighten' || previewTargetsInstant) &&
      !showRegionBonuses &&
      (!!selectedTerritoryId || (previewTargetsInstant && previewTargetIds.length > 0))
    return getSelectionDimmedTerritoryIds(
      geomTerritories.map((territory) => territory.id),
      active
        ? [
            ...(selectedTerritoryId ? [selectedTerritoryId] : []),
            ...(secondaryTerritoryId ? [secondaryTerritoryId] : []),
            ...previewTargetIds,
          ]
        : [],
    )
  }, [
    geomTerritories,
    previewTargetIds,
    previewTargetsInstant,
    secondaryTerritoryId,
    selectedFillMode,
    selectedTerritoryId,
    showRegionBonuses,
  ])
  const selectionDimmedTerritorySet = React.useMemo(
    () => new Set(selectionDimmedTerritoryIds),
    [selectionDimmedTerritoryIds],
  )
  // The Warfish.net logo on Warfish Classics boards: it dims with the board and
  // opens the classics explainer when tapped.
  const logoShapes = React.useMemo(
    () => splitDecorations(state.map.decorations).logo.map((decoration) => decoration.shape),
    [state.map.decorations],
  )
  const [aboutVisible, setAboutVisible] = React.useState(false)
  const openAbout = React.useCallback(() => setAboutVisible(true), [])
  const oneWaySet = React.useMemo(() => {
    if (!selectedTerritoryId) return new Set<string>()
    const sel = geomTerritories.find((t) => t.id === selectedTerritoryId)
    return new Set(sel?.oneWayBorders ?? [])
  }, [selectedTerritoryId, geomTerritories])

  // Territories the viewer can currently act on (place / attack / fortify) —
  // used on web to show a pointer cursor on hover so it's clear what's clickable.
  const validTargetSet = React.useMemo(
    () => new Set(validTargetIds),
    [validTargetIds],
  )
  // Fog of war: territories outside the viewer's vision (hydrated with
  // `fogged`). Keyed off the LIVE territories, not the pinned geometry, since
  // fog moves as ownership does. Empty in every non-fog game.
  const foggedIds = React.useMemo(() => foggedTerritoryIds(state.map.territories), [state.map.territories])
  // Heavy fog only (owner unknown): what the fog-lift animation watches.
  const heavyFoggedIds = React.useMemo(
    () => new Set(state.map.territories.filter((t) => t.fogged && !t.ownerId).map((t) => t.id)),
    [state.map.territories],
  )
  // Web pointer cursor on the currently-actionable territories (a valid target
  // for this phase, or the selected one you can click to deselect). Shared by
  // the territory shapes and their ownership markers, so the hand shows on the
  // marker disc too, not just the territory around it.
  const showsPointer = (territoryId: string) =>
    Platform.OS === 'web' &&
    !!onTerritoryPress &&
    (allTerritoriesInteractive ||
      validTargetSet.has(territoryId) ||
      territoryId === selectedTerritoryId)
  // Preview targets — exactly the ids the caller asked to ring, with no
  // filtering here. MapView used to strip `selectedTerritoryId` on the theory
  // that a source should never ring itself, but that is the CALLER's call and
  // both callers already get it right for their own case: the phase UIs derive
  // targets from `borderReach`, which never contains the source, while the
  // snapshot rings every territory a placement landed on — including the one it
  // also marks as primary.

  // Region → stroke color mapping. Uses greedy graph-colouring so that
  // adjacent regions always receive maximally-distinct palette colours.
  // The adjacency graph is derived from territory adjacency data so no
  // extra map metadata is required. `highlightRegions` overrides from
  // the Map Details screen are applied on top (caller wins).
  //
  // The base graph-colouring result is memoised at MODULE level via
  // getCachedRegionColors (keyed by geometryVersion + regionCount) so the expensive
  // O(R²) ΔE76 scoring only runs once per unique map across all renders.
  // The useMemo here is still needed to apply highlightRegions overrides
  // cheaply on every render without re-running the full scoring pass.
  const regionColorById = React.useMemo(() => {
    const adjGraph = buildRegionAdjacency(geomTerritories, geomRegions)
    const graphColors = getCachedRegionColors(geometryVersion, geomRegions, adjGraph)
    if (!highlightRegions) return graphColors
    const out: Record<string, string> = { ...graphColors }
    for (const [id, color] of Object.entries(highlightRegions)) {
      out[id] = color
    }
    return out
  }, [geometryVersion, geomTerritories, geomRegions, highlightRegions])

  // The live source marker uses the same non-lightened outline that its
  // territory has in the canonical polygon layer. Keep this derivation next
  // to the region palette so backdrop-specific stroke overrides cannot drift
  // from the selected-territory mark.
  const normalTerritoryStrokeColor = React.useCallback(
    (territory: (typeof geomTerritories)[number]) => {
      const backdropStroke =
        state.map.backgroundImage && territory.regionId
          ? BACKDROP_TERRITORY_STROKE_OVERRIDES[territory.regionId] ??
            regionColorById[territory.regionId]
          : undefined
      return (
        backdropStroke ??
        regionColorById[territory.regionId ?? ''] ??
        (state.map.backgroundImage ? 'rgba(30,30,30,0.4)' : palette.border)
      )
    },
    [palette.border, regionColorById, state.map.backgroundImage],
  )

  // Sea-route detection. An adjacency is a sea route when the two
  // territories' polygons don't actually share an edge on the map —
  // i.e. they're connected in the game graph but separated by water in
  // the picture.
  //
  // The connector line anchors to the CLOSEST pair of vertices between
  // the two polygons — by definition the narrowest gap, which is
  // always over water and never crosses a third territory. A naive
  // "nearest-vertex-to-other-badge" picks vertices on the far side of
  // each polygon for non-convex shapes (e.g. Hokkaido's many points)
  // and the line then sails across other islands.
  // All inputs are static per geometry version (geometry + viewBox), so this is
  // computed once per map revision and reused across renders and phase remounts. See
  // getCachedSeaRoutes / computeSeaRoutes above. Keyed on the geometry snapshot
  // rather than the live array, so army updates do not rebuild the route list.
  const seaRoutes = React.useMemo(
    () =>
      getCachedSeaRoutes(
        geometryVersion,
        geomTerritories,
        state.map.wraparoundAdjacencies,
        state.map.suppressSeaRoutes,
        state.map.forceSeaRoutes,
        state.map.forceSeaRouteTuning,
        mapX,
        mapW,
        getPrecomputedSeaRoutes(
          state.map.id,
          state.map.seaRoutes,
          state.map.wraparoundAdjacencies,
          geometryVersion,
          mapX,
          mapW,
          state.map.viewBox,
        ),
      ),
    [
      geometryVersion,
      state.map.id,
      state.map.seaRoutes,
      state.map.wraparoundAdjacencies,
      geomTerritories,
      state.map.suppressSeaRoutes,
      state.map.forceSeaRoutes,
      state.map.forceSeaRouteTuning,
      mapX,
      mapW,
    ],
  )

  const slotByPlayerId = React.useMemo(() => {
    const m: Record<string, number> = {}
    for (const pid of state.players) {
      const slot = parseInt(pid, 10)
      if (!isNaN(slot)) m[pid] = slot
    }
    return m
  }, [state.players])

  // Color is decoupled from the engine's seat/turn slot: playerId (= String(slot))
  // maps to the player's CHOSEN color slot via context. Empty context → identity
  // (each seat renders its own slot color), the correct fallback for standalone
  // MapView usages (map inspector / previews) with no game color context.
  const colorBySlot = useColorBySlot()
  const colorByPlayerId = React.useMemo(() => {
    const m: Record<string, number> = {}
    for (const pid of state.players) {
      const slot = parseInt(pid, 10)
      if (!isNaN(slot)) m[pid] = colorBySlot[slot] ?? slot
    }
    return m
  }, [state.players, colorBySlot])

  // Resolve ownership from live state: the geometry snapshot intentionally
  // does not update when a territory changes hands. Respect chosen player colors.
  const sourceOwnerId = state.map.territories.find(t => t.id === selectedTerritoryId)?.ownerId
  const sourceGlowColor = lightenHex(ownerFill(sourceOwnerId ?? '', colorByPlayerId, palette.unowned), 0.6)
  const innerGlowEnabled = mapInnerGlowEnabled(state.map)
  const ctfTargetCircles = state.map.id === 'captureflag' && !innerGlowEnabled

  // Build a shape lookup for the flash layer (territory id → SVG path `d`).
  const shapeById = React.useMemo(() => {
    const m: Record<string, string | undefined> = {}
    for (const t of geomTerritories) m[t.id] = t.shape
    return m
  }, [geomTerritories])

  // Corrected badge positions for the persistent troop count.
  // Uses computeAlignedBadgePositions which:
  //   (a) Always anchors to the pole of inaccessibility (the point
  //       furthest from any polygon edge), not the blueprint centroid.
  //       The blueprint position is a stale approximation; PoI is always
  //       strictly inside and equidistant from surrounding borders.
  //   (b) Runs a second pass that groups adjacent territories into
  //       horizontal rows / vertical columns and snaps their badge
  //       coordinates to the group median, so aligned blocks (e.g. the
  //       Three Great Halls) share a y (or x) coordinate.
  //       Badges that would fall outside the polygon after snapping
  //       revert to their per-territory PoI automatically.
  // Computed once per territory set; O(1) per territory per frame.
  const badgePositionById = React.useMemo(() => {
    // FULLY-ANCHORED FAST PATH.
    //
    // `applyBadgeAnchors` overwrites the computed position of every territory
    // that carries a `badgeAnchor`, so on a map where all of them do, the entire
    // result of `computeAlignedBadgePositions` is discarded — every single entry.
    // We were paying for it anyway: it is a 32x32 grid pole-of-inaccessibility
    // search per territory, measured at 947ms on world-classic and the single
    // largest cost of opening a map.
    //
    // Anchors are baked by `pnpm map-editor:anchors`, which writes the value the
    // GAME's own algorithm produces (see tools/map-editor/bake-badge-anchors.mts),
    // so the fast path returns exactly what the slow path would have. Every map
    // currently ships fully anchored; a map that isn't (a new one mid-authoring)
    // silently keeps the old behaviour.
    const allAnchored = geomTerritories.every((t) => t.badgeAnchor !== undefined)
    if (allAnchored) return applyBadgeAnchors({}, geomTerritories)
    return applyBadgeAnchors(
      getCachedBadgePositions(geometryVersion, geomTerritories),
      geomTerritories,
    )
  }, [geometryVersion, geomTerritories])

  // Fog lift (FogLiftLayer): what to fade off a territory leaving heavy fog —
  // its disc on marker maps, its outline on filled maps.
  const fogLiftShapeOf = (territoryId: string): FogLiftShape | null => {
    const territory = state.map.territories.find((t) => t.id === territoryId)
    if (!territory) return null
    if (state.map.territoryMarkers) {
      const pos = badgePositionById[territoryId] ?? territory.position
      const metrics = territoryMarkerMetrics(state.map.territoryMarkers.radius, isLandscape, desktop)
      return { id: territoryId, cx: pos.x, cy: pos.y, r: metrics.radius, strokeWidth: metrics.strokeWidth }
    }
    const d = shapeById[territoryId]
    return d ? { id: territoryId, d } : null
  }

  // Polygon area per territory — used only to scale the troop-badge font.
  // Static per map (depends solely on shape), so cached by mapId rather than
  // re-parsing every shape inside the per-territory render loop each frame.
  const areaById = React.useMemo(
    () => getCachedAreas(geometryVersion, geomTerritories),
    [geometryVersion, geomTerritories],
  )

  // Position lookup for the live animation layers (placement / attack /
  // fortify). Prefer the rendered badge position: a badgeAnchor override
  // moves the troop-count marker, and an animation anchored on the stale
  // blueprint centroid visibly detaches from it (the fortify trail was
  // reported starting at the OLD marker spot on anchored maps). The raw
  // centroid remains the fallback for any territory without a computed
  // badge position.
  const positionById = React.useMemo(() => {
    const m: Record<string, { x: number; y: number } | undefined> = {}
    for (const t of geomTerritories) m[t.id] = badgePositionById[t.id] ?? t.position
    return m
  }, [geomTerritories, badgePositionById])

  // Region bonus badge anchors — computed once per region set. Each anchor
  // is the pole of inaccessibility of the region's union shape (if present)
  // or the average of territory centroids (fallback). Only computed when
  // showRegionBonuses is true to avoid the PoI grid-search cost when the
  // overlay is off.
  const regionBonusAnchors = React.useMemo(
    () =>
      showRegionBonuses
        ? getCachedRegionBonusAnchors(geometryVersion, geomRegions, geomTerritories)
        : null,
    [showRegionBonuses, geometryVersion, geomRegions, geomTerritories],
  )

  const playerColorHex = React.useCallback(
    (slot: number) => PLAYER_COLORS[clampPlayerSlot(slot)].hex,
    [],
  )

  // Keep the dispatcher stable even when the phase UI recreates its selection
  // callback. AttackUI and FortifyUI close over selected source/target state,
  // so their callback identity changes on every tap. Passing that changing
  // function to every TerritoryShape defeats the per-territory memo exactly
  // during source → destination selection — the interaction path where dense
  // maps are most expensive. The ref gives the stable dispatcher the latest
  // callback without making all territory nodes reconcile just for identity.
  const onTerritoryPressRef = React.useRef(onTerritoryPress)
  onTerritoryPressRef.current = onTerritoryPress

  const pressNonceRef = React.useRef(0)
  const [pressFeedback, setPressFeedback] = React.useState<{
    territoryId: string
    nonce: number
  } | null>(null)

  const handleTerritoryPress = React.useCallback(
    (territoryId: string) => {
      pressNonceRef.current += 1
      setPressFeedback({ territoryId, nonce: pressNonceRef.current })
      onTerritoryPressRef.current?.(territoryId)
    },
    [],
  )

  const toSelectionTarget = (
    territory: (typeof geomTerritories)[number] | undefined,
  ): SelectionEmphasisTarget | undefined =>
    territory
      ? {
          id: territory.id,
          shape: territory.shape,
          positionX: (badgePositionById[territory.id] ?? territory.position).x,
          positionY: (badgePositionById[territory.id] ?? territory.position).y,
          normalStrokeColor: normalTerritoryStrokeColor(territory),
        }
      : undefined
  const selectionSource = toSelectionTarget(
    selectedTerritoryId
      ? geomTerritories.find((territory) => territory.id === selectedTerritoryId)
      : undefined,
  )
  const pressedTarget = toSelectionTarget(
    pressFeedback
      ? geomTerritories.find((territory) => territory.id === pressFeedback.territoryId)
      : undefined,
  )

  // Selection overlay (dim, target glow, source hatch). It is drawn in its own
  // <Svg> above the board and faded by native view opacity — see
  // MapOverlayLayer for why. One snapshot value, so the overlay can keep
  // drawing the previous selection while it fades out.
  const selectionOverlay = React.useMemo(() => ({
    dimIds: selectionDimmedTerritoryIds,
    previewTargetIds,
    selectedTerritoryId,
    secondaryTerritoryId,
    sourceGlowColor,
    sourceDark: colorByPlayerId[sourceOwnerId ?? ''] === 3,
  }), [selectionDimmedTerritoryIds, previewTargetIds, selectedTerritoryId, secondaryTerritoryId,
    sourceGlowColor, colorByPlayerId, sourceOwnerId])
  const selectionOverlayActive = selectionDimmedTerritoryIds.length > 0 || previewTargetIds.length > 0 ||
    !!selectedTerritoryId || !!secondaryTerritoryId
  const { shown: shownSelection, style: selectionFadeStyle } =
    useOverlayFade(selectionOverlay, selectionOverlayActive, previewTargetsInstant)


  // The event-animation hooks + layers live in the memoized
  // <MapAnimationLayers> sibling (rendered inside the SVG below) so their
  // self-driven state churn doesn't re-render the whole board. See that
  // component for the rationale.

  // Flash stroke width: 2× the region-perimeter stroke so the flash
  // reads as a "ring outside the region ring" rather than competing
  // with it.
  const conquestFlashStrokeWidth = regionBorderWidth * 2

  // ── Zoom / pan ────────────────────────────────────────────────────────────
  // The PanResponder converts touch-pixel deltas (screen pixels) to
  // viewBox-unit deltas so the content moves in sync with the finger
  // regardless of the SVG's pixel-per-viewBox-unit scale factor.
  //
  // Ratio: containerWidth (px) covers mapW (viewBox units)
  //   → 1 px = mapW / containerWidth viewBox units
  //
  // The responder accumulates deltas on top of the CURRENT panOffset
  // passed in as props (the parent owns the authoritative state).
  // We store the base offset at gesture start in a ref so we can
  // compute absolute moves rather than summing floating errors.

  /** Pan base at the start of the current drag gesture (viewBox units). */
  const panBase = React.useRef<PanOffset>({ tx: 0, ty: 0 })

  // Always-fresh reference to the latest panOffset. The panResponder is
  // memoised (so its callbacks don't recreate on every render); without
  // this ref the Grant callback would close over the FIRST panOffset
  // forever and every new drag would start from origin instead of
  // continuing where the previous drag left off.
  /** Latest offset produced by the in-flight drag, committed on release. */
  const panLatest = React.useRef<PanOffset | null>(null)
  const panOffsetRef = React.useRef<PanOffset>(panOffset ?? { tx: 0, ty: 0 })
  panOffsetRef.current = panOffset ?? { tx: 0, ty: 0 }
  const onPanChangeRef = React.useRef(onPanChange)
  onPanChangeRef.current = onPanChange

  // Current zoom scale, defaulting when the caller doesn't drive it. Kept in a
  // ref so the memoised pan responder reads the live value (same reason as
  // panOffsetRef above).
  const effectiveZoomScale = zoomScale ?? DEFAULT_ZOOM_SCALE

  // Rendered screen pixels per viewBox unit, at the CURRENT zoom. Anything that
  // must come out a fixed size on screen — the target-preview brackets, the
  // native tap slop — divides its pixel budget by this instead of expressing
  // itself in viewBox units, which would grow and shrink with the map.
  const pxPerUnit = (svgWidth / mapW) * effectiveZoomScale
  // A non-zoomable map does not apply the remembered/default zoom transform.
  const glowUnitsPerPixel = mapW / svgWidth / (zoomEnabled ? effectiveZoomScale : 1)
  const zoomScaleRef = React.useRef(effectiveZoomScale)
  zoomScaleRef.current = effectiveZoomScale

  // Drag-hint compass — briefly fades in an NSWE arrow cluster over the map the
  // moment the player zooms IN (scale increases past the fit level), signalling
  // that the map is now pannable. Fades out on its own.
  const dragHintOpacity = React.useRef(new Animated.Value(0)).current
  const prevScaleRef = React.useRef(effectiveZoomScale)
  React.useEffect(() => {
    const prev = prevScaleRef.current
    prevScaleRef.current = effectiveZoomScale
    if (
      zoomEnabled &&
      effectiveZoomScale > prev + 1e-6 &&
      effectiveZoomScale > MIN_ZOOM_SCALE + 1e-6 &&
      consumeDragHint()
    ) {
      dragHintOpacity.setValue(1)
      Animated.timing(dragHintOpacity, {
        toValue: 0,
        duration: 1400,
        delay: 500,
        useNativeDriver: Platform.OS !== 'web',
      }).start()
    }
  }, [effectiveZoomScale, zoomEnabled, dragHintOpacity])

  const panResponder = React.useMemo(
    () =>
      PanResponder.create({
        // Never grab on touch start. If we did, a tap on a territory in
        // zoom mode would be captured by the pan responder instead of
        // firing the Path's onPress (attack / fortify / placement all
        // break). Quick taps fall through normally.
        onStartShouldSetPanResponder: () => false,
        // Only claim the gesture once the user has clearly started to
        // drag (a few pixels of movement). Below that threshold the
        // touch is treated as a tap and the territory press handler
        // wins. The threshold is intentionally small — a held finger
        // with no movement won't accidentally pan.
        onMoveShouldSetPanResponder: (_e, g) =>
          zoomEnabled && (Math.abs(g.dx) > 4 || Math.abs(g.dy) > 4),
        onPanResponderGrant: () => {
          // Snapshot the CURRENT offset (via ref so it's always fresh)
          // as the base for this gesture. Without this each new drag
          // would re-anchor at the panOffset captured on first render.
          // panLatest wins when it is set: the caller may not have committed the
          // previous drag (onPanChange is optional), and the prop-sync effect
          // clears it as soon as the prop does catch up.
          panBase.current = panLatest.current ?? panOffsetRef.current
        },
        onPanResponderMove: (_e, gestureState) => {
          if (!onPanChange) return
          // NO setState here, and no onPanChange — see "Where the pan lives".
          // Both used to fire on every move event, re-rendering the whole game
          // screen ~16 times a second for a gesture that changes nothing but a
          // transform.
          // Convert screen-pixel deltas to viewBox-unit deltas.
          const newOffset = panFromScreenDelta(
            mapX,
            mapY,
            mapW,
            mapH,
            zoomScaleRef.current,
            containerWidth,
            panBase.current,
            gestureState.dx,
            gestureState.dy,
          )
          panLatest.current = newOffset
          applyPanTransform(newOffset.tx, newOffset.ty, zoomScaleRef.current)
        },
        onPanResponderRelease: () => {
          // Commit ONCE, on release, so the caller stays authoritative for the
          // zoom buttons and re-centring while the drag itself costs it nothing.
          if (onPanChange && panLatest.current) onPanChange(panLatest.current)
        },
        // Once we've claimed the drag, never yield it back. Without this
        // an ancestor ScrollView (e.g. the portrait map-detail screen,
        // where the map sits above a scrollable region list) can reclaim
        // the responder mid-drag and scroll the whole page instead of
        // panning the map. A map drag should ONLY ever pan the map.
        onPanResponderTerminationRequest: () => false,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zoomEnabled, onPanChange, containerWidth, mapW, mapH],
  )

  // Current effective translate values (viewBox units). When zoom is
  // disabled fall back to (0, 0) so the G transform is a no-op.
  // ── Where the pan lives ───────────────────────────────────────────────────
  //
  // On the zoom/pan <G>, as before — but written IMPERATIVELY, so a drag costs
  // no React render.
  //
  // It used to be written to the CALLER's state on every touch-move event, and
  // that state lives on the game screen, so one drag re-rendered GameRoute,
  // ActiveGameView, the phase UI and the board: measured at 16 cascades a second
  // for a scripted swipe, and a real finger emits more. Nothing about the board
  // changes while you drag. Only a transform does.
  //
  // Two cheaper-looking places to put it are both wrong:
  //
  //   • Local React state still re-renders THIS component once per touch frame
  //     (92 renders for three swipes). The memoised children bail out, but React
  //     still reconciles the ~500 elements this component returns, every frame,
  //     on the thread the animations run on.
  //
  //   • A native transform on the view WRAPPING the <Svg> renders nothing, but it
  //     moves the canvas itself rather than the content inside it — so the
  //     trailing edge stops covering the container and the board is cut off along
  //     a hard line with the background showing through. The SVG viewport is what
  //     clips a pan correctly, and only content panned INSIDE it gets that.
  //
  // So the transform stays on the <G> and the gesture pushes it straight to the
  // native view. Send a numeric SVG matrix: G.setNativeProps otherwise forwards
  // the raw string as well as its extracted matrix, crashing Android's inherited
  // View transform setter. React-rendered G props still use the SVG parser.
  const zoomGroupRef = React.useRef<React.ComponentRef<typeof G>>(null)
  // The overlay layers' zoom/pan groups move with the board's during a drag.
  const selectionGroupRef = React.useRef<React.ComponentRef<typeof G>>(null)
  const effectsGroupRef = React.useRef<React.ComponentRef<typeof G>>(null)
  const badgesGroupRef = React.useRef<React.ComponentRef<typeof G>>(null)
  const slopGroupRef = React.useRef<React.ComponentRef<typeof G>>(null)
  const resultsGroupRef = React.useRef<React.ComponentRef<typeof G>>(null)
  // Overlays mounted on demand (conquest flashes) register their groups here.
  const transientGroups = React.useRef(new Set<React.ComponentRef<typeof G>>())
  const registerTransientGroup = React.useCallback((group: React.ComponentRef<typeof G> | null) => {
    if (!group) return
    transientGroups.current.add(group)
    return () => { transientGroups.current.delete(group) }
  }, [])
  // Not set*: it writes native props, never React state (mapPanRenderCost test).
  const applyPanTransform = (tx: number, ty: number, scale: number) => {
    const props = panNativeProps(tx, ty, scale, Platform.OS)
    for (const group of [zoomGroupRef, selectionGroupRef, effectsGroupRef, badgesGroupRef, resultsGroupRef, slopGroupRef]) {
      group.current?.setNativeProps(props)
    }
    for (const group of transientGroups.current) group.setNativeProps(props)
  }
  const propTx = zoomEnabled ? (panOffset?.tx ?? 0) : 0
  const propTy = zoomEnabled ? (panOffset?.ty ?? 0) : 0
  // A prop CHANGE always wins over an in-flight gesture: re-centring, the zoom
  // buttons and region focus all arrive that way, and so does our own commit on
  // release. Comparing against the last prop we saw — rather than against
  // panLatest — keeps a caller that ignores onPanChange working, since its
  // unchanging prop then never overrides the gesture.
  const lastProp = React.useRef({ tx: propTx, ty: propTy })
  if (lastProp.current.tx !== propTx || lastProp.current.ty !== propTy) {
    lastProp.current = { tx: propTx, ty: propTy }
    panLatest.current = null
  }
  // Reading panLatest during render is deliberate. An unrelated commit — a bot's
  // move, a socket patch — must not snap a drag in progress back to the last
  // offset the caller knows about.
  const liveOffset = panLatest.current ?? panOffset
  const activeTx = zoomEnabled ? (liveOffset?.tx ?? 0) : 0
  const activeTy = zoomEnabled ? (liveOffset?.ty ?? 0) : 0



  // On-map +/- zoom. Adjusts the scale by one step, recentres the pan so the
  // current view centre stays put, and reports both up to the caller.
  const handleZoomButton = React.useCallback(
    (direction: 1 | -1) => {
      const old = zoomScaleRef.current
      const next = clampZoomScale(old + direction * ZOOM_STEP)
      if (next === old) return
      onZoomScaleChange?.(next)
      onPanChange?.(
        rescaleAboutCenter(mapX, mapY, mapW, mapH, old, next, panOffsetRef.current),
      )
    },
    [onZoomScaleChange, onPanChange, mapX, mapY, mapW, mapH],
  )
  const canZoomIn = effectiveZoomScale < MAX_ZOOM_SCALE - 1e-6
  const canZoomOut = effectiveZoomScale > MIN_ZOOM_SCALE + 1e-6

  // ── Per-instance clip-id namespace ──────────────────────────────────────
  // <ClipPath> ids live in the GLOBAL SVG id space. Many screens mount more
  // than one MapView at once (portrait/landscape variants, phase UIs, the
  // historical/time-machine view alongside the live board), and on web each
  // emits `clip-region-<id>` for the SAME map — duplicate ids. The browser
  // then resolves `url(#clip-region-pacific)` to whichever duplicate it binds
  // first, which can be a hidden/empty sibling, so the region-perimeter stroke
  // renders UNCLIPPED (full width spilling past every coastline → thick/fuzzy
  // borders) until a full page reload collapses the tree to one instance.
  // Namespacing every clip id with a per-instance React.useId() makes each
  // MapView's clips unique, so references always resolve to this instance's
  // own clip on the very first paint. Sanitised (useId() contains ':').
  const evenOdd = state.map.backgroundImage ? ('evenodd' as const) : undefined

  const clipNS = `m${React.useId().replace(/:/g, '')}-`

  // Sea-route lines — geometry (from the mapId-keyed cache) plus theme colours,
  // so nothing a game does can change them. Each route draws two strokes (halo +
  // line), and they were being rebuilt on every commit.
  // Routes whose border rolls custom dice (Territory.borderDice) are coloured by
  // what the dice do to the attacker: orange for worse (Royal Rumble's 5 v 6
  // islet crossings), red for better. Keyed "a|b" with the ids sorted.
  const diceRouteKinds = React.useMemo(() => customDiceRouteKinds(state.map.territories), [state.map.territories])

  const seaRouteLayer = React.useMemo(() => {
    if (state.map.territoryMarkers || state.map.hideAdjacencyLines || state.map.id === 'forbidden-city') return null
    const baseW = Math.max(0.6, sizingWidth * 0.002)
    const popW = baseW * 1.3   // crossing line (lighter weight)
    const haloW = popW + Math.max(0.8, baseW * 0.9)   // thin contrast outline
    const arrowSize = baseW * 5.6
    // Territory strokes are clipped to their polygons, so the rendered coast is
    // the polygon edge, plus whatever bridge strokes or the traced-border
    // overlay paint past it. Arrowheads stop that far off the coast.
    const edge = Math.max(
      (state.map.edgeConfig?.bridgeStrokeWidth ?? 0) / 2,
      state.map.borderShape && !state.map.backgroundImage ? Math.max(1, sizingWidth * 0.002) / 2 : 0,
    )
    const ringsById = new Map(geomTerritories.map((t) => [t.id, outlineRings(t.shape)] as const))
    // Sea-route outline: matches the theme, unless the map keeps the
    // dark-mode outline on both backgrounds (MapDef.darkSeaRoutes, Arctic).
    const routeHalo = state.map.darkSeaRoutes || theme === 'dark' ? '#0a1610' : '#fffbeb'
    const waterLines: React.ReactNode[] = []
    const gateLines: React.ReactNode[] = []
    const arrows: React.ReactNode[] = []
    for (const route of seaRoutes) {
      const r = clearRouteEnds(route, ringsById, { edge, cap: haloW / 2, arrowSize, arrowStroke: haloW - popW })
      // A one-way line stops inside its arrowhead, so its round cap can't
      // poke out past the tip.
      const lineEnd = r.oneWay ? routeLineEnd(r, arrowSize * 0.5) : r.to
      const diceKind = diceRouteKinds.get(routePairKey(r.aId, r.bId))
      const routeStroke = diceKind ? mapSymbolColor(diceKind, theme === 'dark' ? 'dark' : 'light') : SEA_ROUTE_STROKE
      // Sea routes are solid sky-blue against the ocean. Render TWO
      // strokes: a thicker contrast halo underneath (dark on light
      // theme, light on dark theme) so the connector pops on either
      // ocean — then the bright line on top at full opacity.
      ;(r.clipToWater ? waterLines : gateLines).push(
        <G key={`searoute-${r.aId}-${r.bId}`}>
          <Line x1={r.from.x} y1={r.from.y} x2={lineEnd.x} y2={lineEnd.y} stroke={routeHalo} strokeWidth={haloW} strokeOpacity={0.9} strokeLinecap="round" />
          <Line x1={r.from.x} y1={r.from.y} x2={lineEnd.x} y2={lineEnd.y} stroke={routeStroke} strokeWidth={popW} strokeOpacity={1} strokeLinecap="round" />
        </G>,
      )
      // One-way crossing (Territory.oneWayBorders): an arrowhead at the target end.
      if (r.oneWay) {
        arrows.push(
          <Polygon
            key={`searoute-arrow-${r.aId}-${r.bId}`}
            points={oneWayArrowPoints(r, arrowSize)}
            fill={routeStroke}
            stroke={routeHalo}
            strokeWidth={haloW - popW}
            strokeLinejoin="round"
          />,
        )
      }
    }
    // Lines run into the land and are clipped to the water, so each is cut
    // exactly at the rendered coast: a surrounding rectangle plus every
    // territory, under even-odd, leaves only the sea.
    const pad = mapW
    const water = `M${mapX - pad} ${mapY - pad}h${mapW + 2 * pad}v${mapH + 2 * pad}h${-mapW - 2 * pad}Z ${geomTerritories.map((t) => t.shape ?? '').join(' ')}`
    return (
      <G pointerEvents="none">
        <Defs>
          <ClipPath id={`${clipNS}sea-route-water`}>
            <Path d={water} fillRule="evenodd" clipRule="evenodd" />
          </ClipPath>
        </Defs>
        <G clipPath={`url(#${clipNS}sea-route-water)`}>{waterLines}</G>
        {gateLines}
        {arrows}
      </G>
    )
  }, [seaRoutes, state.map.territoryMarkers, state.map.hideAdjacencyLines, state.map.id, state.map.darkSeaRoutes, state.map.borderShape, state.map.backgroundImage, state.map.edgeConfig?.bridgeStrokeWidth, geomTerritories, palette, theme, mapX, mapY, mapW, mapH, clipNS, diceRouteKinds])

  const borderChevronLayer = React.useMemo(() => {
    const chevrons = state.map.borderChevronMarks ?? computeBorderChevrons(state.map)
    if (chevrons.length === 0) return null
    const size = Math.max(4, sizingWidth * 0.0085)
    return (
      <G pointerEvents="none">
        {chevrons.map((c, i) => {
          const points = borderChevronPoints(c, size)
          return (
            <G key={`border-chevron-${i}`}>
              <Polyline points={points} fill="none" stroke={MAP_SYMBOL_COLORS.chevronHalo} strokeWidth={borderChevronHaloWidth(size)} strokeLinecap="round" strokeLinejoin="round" strokeOpacity={0.85} />
              <Polyline points={points} fill="none" stroke={MAP_SYMBOL_COLORS.advantage} strokeWidth={size * 0.28} strokeLinecap="round" strokeLinejoin="round" />
            </G>
          )
        })}
      </G>
    )
  }, [state.map, sizingWidth, theme])

  // Continent fill — one <Path> per region, and nothing about it can change
  // during a game.
  const continentFillLayer = React.useMemo(() => {
    if (state.map.noContinentFill || state.map.backgroundImage) return null
    return geomRegions.map((region) =>
      region.shape ? (
        <Path
          key={`region-${region.id}`}
          d={region.shape}
          fill={palette.continent}
          stroke="transparent"
          pointerEvents="none"
        />
      ) : null,
    )
  }, [geomRegions, state.map.noContinentFill, state.map.backgroundImage, palette.continent])

  // Per-territory clip paths — pure geometry, one per territory, plus one per
  // region. On Forbidden City that is 158 <ClipPath> + <Path> pairs rebuilt on
  // every socket message before this memo.
  // ── Native tap slop ────────────────────────────────────────────────
  // Small territories are hard to hit with a finger. react-native-svg
  // hit-tests a single point against the polygon, so a tap that lands a
  // few pixels outside a 20 px cell simply misses — and on a map like
  // Capture the Flag the cells that decide the game are the small ones.
  //
  // Each small actionable territory gets a transparent stroked outline.
  // On both platforms the slop is clipped outside ALL actionable territory
  // interiors, leaving shared internal boundaries to the real polygons.
  // The stroke widens the hit area outward by
  // TAP_SLOP_PX and paints nothing (iOS builds its hit area from the
  // path plus its stroke band, and a transparent stroke still counts).
  //
  // The exclusion includes large cells too, so they never lose an inside
  // edge to a small clickable neighbor's expanded outline.
  //
  // The threshold and the width are both in SCREEN px and converted to
  // viewBox units through the live zoom, so zooming in shrinks the slop
  // rather than dragging it along: once a cell is genuinely big on
  // screen it stops getting help. */}
  //
  // Memoised on the geometry + the live zoom, so it is rebuilt when the map or
  // the zoom changes and NOT when an army moves.
  const tapSlopLayer = React.useMemo(() => {
    if (!onTerritoryPress) return null
    if (!isFinite(pxPerUnit) || pxPerUnit <= 0) return null
    // Stroke straddles the path, so half of it lies outside.
    const slopWidth = (TAP_SLOP_PX * 2) / pxPerUnit
    const actionable = geomTerritories.filter((territory) =>
      allTerritoriesInteractive || validTargetSet.has(territory.id) ||
      territory.id === selectedTerritoryId || territory.id === secondaryTerritoryId)
    // A surrounding rectangle plus the map's disjoint territory interiors,
    // under even-odd fill, leaves only the outside of the clickable union.
    // Existing annular subpaths retain their holes correctly.
    const outer = `M${mapX - slopWidth} ${mapY - slopWidth}h${mapW + 2 * slopWidth}v${mapH + 2 * slopWidth}h${-mapW - 2 * slopWidth}Z`
    const interiors = actionable.map((territory) => {
      if (territory.shape) return territory.shape
      const { x, y } = territory.position
      const r = badgeRadius * 1.5
      return `M${x - r} ${y}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0Z`
    }).join(' ')
    const slopAccessibilityProps: {
      'aria-hidden'?: boolean
      accessibilityElementsHidden?: boolean
      importantForAccessibility?: 'no'
    } = Platform.OS === 'web'
      ? { 'aria-hidden': true }
      : { accessibilityElementsHidden: true, importantForAccessibility: 'no' }
    return <>
      <Defs><ClipPath id={`${clipNS}tap-slop-outside`}>
        <Path d={`${outer} ${interiors}`} fillRule="evenodd" clipRule="evenodd" />
      </ClipPath></Defs>
      <G clipPath={`url(#${clipNS}tap-slop-outside)`}>
      {actionable.map((territory) => {
      if (!territory.shape) return null
      const area = areaById[territory.id] ?? 0
      if (area <= 0) return null
      if (Math.sqrt(area) * pxPerUnit >= TAP_SLOP_SMALL_PX) return null
      return (
        <G
          key={`slop-${territory.id}`}
          testID={`territory-tap-slop-${territory.id}`}
          onPress={() => handleTerritoryPress(territory.id)}
          // Same even-odd rule the real polygon uses, on the group as well as
          // the path — otherwise a small ANNULAR territory's slop ring would
          // reclaim the hole its own polygon correctly gives up (see
          // TerritoryShape).
          fillRule={evenOdd}
          {...slopAccessibilityProps}
        >
          <Path
            d={territory.shape}
            fill="none"
            fillRule={evenOdd}
            stroke="transparent"
            strokeWidth={slopWidth}
            strokeLinejoin="round"
          />
        </G>
      )
    })}
      </G>
    </>
  }, [geomTerritories, areaById, pxPerUnit, onTerritoryPress, handleTerritoryPress, evenOdd,
    allTerritoriesInteractive, validTargetSet, selectedTerritoryId, secondaryTerritoryId, badgeRadius,
    mapX, mapY, mapW, mapH, clipNS])

  // Bridge strokes — pure geometry, gated on one scalar from the map's own
  // edgeConfig. Rebuilding these per socket message repainted every territory
  // outline for nothing.
  const bridgeStrokeWidth = state.map.edgeConfig?.bridgeStrokeWidth ?? 0
  const bridgeLayer = React.useMemo(() => {
    if (bridgeStrokeWidth <= 0) return null
    return geomTerritories.map((t) =>
      t.shape ? (
        <Path
          key={`bridge-${t.id}`}
          d={t.shape}
          fill={palette.border}
          stroke={palette.border}
          strokeWidth={bridgeStrokeWidth}
          strokeLinejoin="round"
          pointerEvents="none"
        />
      ) : null,
    )
  }, [geomTerritories, bridgeStrokeWidth, palette.border])

  const clipDefs = React.useMemo(
    () => (
      <>
        {geomTerritories.map((t) =>
          t.shape ? (
            <ClipPath key={`clip-${t.id}`} id={`${clipNS}clip-${t.id}`}>
              {/* even-odd so a hole (annular territory) is excluded from the
                  clip too, matching the even-odd fill above. */}
              <Path d={t.shape} fillRule={evenOdd} />
            </ClipPath>
          ) : null,
        )}
        {geomRegions.map((r) =>
          r.shape ? (
            <ClipPath key={`clip-region-${r.id}`} id={`${clipNS}clip-region-${r.id}`}>
              <Path d={r.shape} />
            </ClipPath>
          ) : null,
        )}
      </>
    ),
    [geomTerritories, geomRegions, clipNS, evenOdd],
  )

  // Very-light cross-hatch geometry for border (adjacent) territories.
  const hatchGap = Math.max(2.5, sizingWidth * 0.014)

  // Spotlight (regions view) — three brightness tiers:
  //   bright : the selected territory + its bordering (adjacent) territories.
  //   tier1  : the selected region's OTHER (non-border) territories — light fade.
  //   tier2  : everything else (other regions / non-region / non-border) — more.
  // When only a region is selected (no territory), that whole region is bright.
  //
  // A REGION selection lights that region and nothing else. A one-hop "penumbra"
  // over its neighbours was tried (to keep the pedestal court under a fort out of
  // shadow) and rejected by the maintainer: selecting a region from the list must
  // not light adjacent territories. Brightening neighbours stays exclusive to a
  // TERRITORY selection, where it means "these are the borders you can attack".
  const dimTiers = React.useMemo(() => {
    const bright = new Set<string>(adjacentTerritoryIds)
    if (selectedTerritoryId) bright.add(selectedTerritoryId)
    // A one-way border is REACH, exactly like a two-way neighbour — and the
    // callers only ever pass `adjacencies` as `adjacentTerritoryIds`, so a
    // watchtower's one-way cells were the only part of a selection's reach left
    // under the dim. On Capture the Flag that is most of it: each tower holds
    // 10–12 cells one-way against 3–4 two-way. The purple wash that marks them is
    // drawn UNDER the dim, so 0.72 of ocean over it buried the very layer that
    // exists to explain the board's topology.
    for (const id of oneWaySet) bright.add(id)
    const hasTerritorySel = bright.size > 0
    const tier1 = new Set<string>()
    if (selectedRegionId) {
      for (const t of geomTerritories) {
        if (t.regionId !== selectedRegionId) continue
        if (hasTerritorySel) {
          if (!bright.has(t.id)) tier1.add(t.id)
        } else {
          bright.add(t.id)
        }
      }
    }
    return { bright, tier1 }
  }, [adjacentTerritoryIds, oneWaySet, selectedTerritoryId, selectedRegionId, geomTerritories])
  const spotlightActive =
    showRegionBonuses &&
    (!!selectedTerritoryId || !!selectedRegionId || adjacentTerritoryIds.length > 0)
  const isSelectedRegionId = (id: string | null | undefined) =>
    !!id && (id === selectedRegionId || id === regionFocusId)

  // Where the spotlight dim gets drawn. A walls overlay is a full-board raster
  // of the walls + buildings painted last, so a dim underneath it shades the
  // ground and leaves every structure at identical brightness — selecting a
  // fort's region could not make that fort stand out. With an overlay present
  // the dim moves above it so structures shade with their ground. Rendered from
  // one definition in one of two slots, never both.
  const dimAboveWalls = !!state.map.wallsOverlay

  // Structure re-light — the ONE thing that may be painted over the walls raster
  // in the selected region's own colour.
  //
  // Why it has to exist: a territory fill sits UNDER the walls overlay, and that
  // overlay is opaque wherever it paints. On flat ground it paints almost nothing,
  // so a lit flag cell reaches the full chip colour. On a fort it paints the whole
  // structure — 44–63% of each keep is opaque dark line-art — so half the building
  // could never take the tint at all and a selected fort stayed a dark olive lump
  // next to its own bright gold chip.
  //
  // Opacity is the entire design. At 1.0 this is the rejected experiment: the fill
  // paints over the building and a fort becomes a flat coloured silhouette with no
  // stonework left. Below ~1 the near-black contours still show through by
  // (1 − opacity), so the structure reads as LIT rather than as a cut-out. 0.72
  // was chosen to land close to the chip while keeping the roofline visible; it is
  // the knob to turn if the buildings want to be brighter or more detailed.
  //
  // Scoped hard: only the emphasised region, only in regions view, only on a map
  // that HAS a walls overlay (i.e. only where the problem exists), and never over
  // the amber selected territory — selecting the territory already reads correctly
  // and its amber must not be repainted in the region's hue.
  // Selection + geometry only, so a bot's 400ms state patch — which moves armies
  // and nothing else — cannot rebuild a <Path> per territory here.
  const structureRelightLayer = React.useMemo(() => {
    if (!(dimAboveWalls && showRegionBonuses && spotlightActive)) return null
    return geomTerritories.map((territory) => {
          const rid = territory.regionId
          const inSelectedRegion = !!rid && (rid === selectedRegionId || rid === regionFocusId)
          if (!territory.shape || !inSelectedRegion) return null
          if (territory.id === selectedTerritoryId) return null
          // Never paint over the one-way reach wash. A tower's one-way cells
          // include its own base's fort, battery and flag, so with that region
          // focused the re-light would sit straight on top of the purple hatch and
          // the two indicators would fight over the same pixels.
          if (oneWaySet.has(territory.id)) return null
          return (
            <Path
              key={`relight-${territory.id}`}
              d={territory.shape}
              fill={illuminateHex(regionColorById[territory.regionId ?? ''] ?? palette.unowned)}
              fillOpacity={STRUCTURE_RELIGHT_OPACITY}
              fillRule="evenodd"
              pointerEvents="none"
            />
          )
        })
  }, [
    geomTerritories,
    dimAboveWalls,
    showRegionBonuses,
    spotlightActive,
    selectedRegionId,
    regionFocusId,
    selectedTerritoryId,
    oneWaySet,
    regionColorById,
    palette.unowned,
  ])

  const spotlightDimLayer = React.useMemo(() => {
    if (!spotlightActive) return null
    return [
      ...geomTerritories.map((territory) => {
        if (!territory.shape || dimTiers.bright.has(territory.id)) return null
        const fillOpacity = dimTiers.tier1.has(territory.id) ? 0.6 : 0.72
        return (
          <Path
            key={`dim-${territory.id}`}
            d={territory.shape}
            fill={spotlightDim}
            fillOpacity={fillOpacity}
            pointerEvents="none"
          />
        )
      }),
      // The logo recedes with the unlit territories. One composited group, so
      // its overlapping outline and lettering dim once.
      logoShapes.length > 0 ? (
        <G key="dim-warfish-logo" opacity={0.72} pointerEvents="none">
          {logoShapes.map((shape, i) => <Path key={`dim-logo-${i}`} d={shape} fill={spotlightDim} pointerEvents="none" />)}
        </G>
      ) : null,
    ]
  }, [geomTerritories, spotlightActive, dimTiers, spotlightDim, logoShapes])

  // iOS Safari ignores `touch-action: none` on an element nested inside a
  // scroll container — it still scrolls the ancestor (the reported bug: dragging
  // the map on /maps/:id drags the whole page). Enforce ownership by preventing
  // the browser default on touchmove while the map is pannable, so a drag on the
  // map pans the map and never scrolls the page. preventDefault only blocks the
  // browser's scroll. Web pointer events handle the gesture on web; native
  // relies on the PanResponder below.
  const wrapperRef = React.useRef<unknown>(null)
  const webPanPointerId = React.useRef<number | null>(null)
  const webPanStart = React.useRef({ x: 0, y: 0 })
  const webPanMoved = React.useRef(false)
  const webSuppressClick = React.useRef(false)
  React.useEffect(() => {
    if (Platform.OS !== 'web' || !zoomEnabled) return
    const node = wrapperRef.current as (HTMLElement & {
      setPointerCapture?: (pointerId: number) => void
      releasePointerCapture?: (pointerId: number) => void
      hasPointerCapture?: (pointerId: number) => boolean
    }) | null
    if (!node || typeof node.addEventListener !== 'function') return
    type WebPointerEvent = PointerEvent
    // setPointerCapture/releasePointerCapture throw NotFoundError when the id is
    // no longer an active pointer (the browser can retire it between the event
    // firing and this handler running). Optional chaining guards a MISSING
    // method, not a throw, so both calls are wrapped.
    const capture = (pointerId: number) => {
      try {
        node.setPointerCapture?.(pointerId)
      } catch {
        // Non-fatal: without capture we still pan, we just rely on the
        // pointerup landing on this node.
      }
    }
    const release = (pointerId: number) => {
      try {
        if (node.hasPointerCapture?.(pointerId)) node.releasePointerCapture?.(pointerId)
      } catch {
        // Already released or retired — nothing to undo.
      }
    }
    const onPointerDown = (event: WebPointerEvent) => {
      if (webPanPointerId.current !== null) return
      if (event.pointerType === 'mouse' && event.button !== 0) return
      // A later pointerdown means any prior drag's compatibility click has
      // either already been consumed or was not generated by this browser.
      webSuppressClick.current = false
      webPanPointerId.current = event.pointerId
      webPanStart.current = { x: event.clientX, y: event.clientY }
      webPanMoved.current = false
      panBase.current = panLatest.current ?? panOffsetRef.current
    }
    const onPointerMove = (event: WebPointerEvent) => {
      if (webPanPointerId.current !== event.pointerId) return
      const dx = event.clientX - webPanStart.current.x
      const dy = event.clientY - webPanStart.current.y
      if (!webPanMoved.current && Math.abs(dx) <= 4 && Math.abs(dy) <= 4) return
      webPanMoved.current = true
      webSuppressClick.current = true
      event.preventDefault()
      // Capture only once this is definitely a drag. Capturing on pointerdown
      // would retarget the follow-up `click` to this wrapper and break
      // territory taps; past the threshold, suppressing the tap is what we
      // want anyway.
      capture(event.pointerId)
      if (!onPanChangeRef.current) return
      const newOffset = panFromScreenDelta(
        mapX,
        mapY,
        mapW,
        mapH,
        zoomScaleRef.current,
        containerWidth,
        panBase.current,
        dx,
        dy,
      )
      panLatest.current = newOffset
      applyPanTransform(newOffset.tx, newOffset.ty, zoomScaleRef.current)
    }
    const finishPointer = (event: WebPointerEvent, commit: boolean) => {
      if (webPanPointerId.current !== event.pointerId) return
      const moved = webPanMoved.current
      if (commit && moved) event.preventDefault()
      if (commit && moved && onPanChangeRef.current && panLatest.current) {
        onPanChangeRef.current(panLatest.current)
      }
      if (!commit) webSuppressClick.current = false
      release(event.pointerId)
      webPanPointerId.current = null
      webPanMoved.current = false
    }
    const onPointerUp = (event: WebPointerEvent) => finishPointer(event, true)
    const onPointerCancel = (event: WebPointerEvent) => finishPointer(event, false)
    const onClickCapture = (event: MouseEvent) => {
      if (!webSuppressClick.current) return
      // RN-SVG's territory onPress is backed by the browser click on web. A
      // drag must never become an attack/fortify/placement tap when the
      // pointer is released over a territory.
      webSuppressClick.current = false
      event.preventDefault()
      event.stopPropagation()
    }
    const onTouchMove = (event: Event) => event.preventDefault()
    node.addEventListener('pointerdown', onPointerDown)
    node.addEventListener('pointermove', onPointerMove, { passive: false })
    node.addEventListener('pointerup', onPointerUp)
    node.addEventListener('pointercancel', onPointerCancel)
    node.addEventListener('click', onClickCapture, true)
    node.addEventListener('touchmove', onTouchMove, { passive: false })
    // Safety net. Capture is only taken once the drag threshold is crossed, so
    // a press that moves <= 4px and then releases OUTSIDE this node sends its
    // pointerup somewhere else entirely. Without this, webPanPointerId stays
    // set, every later pointerdown early-returns, and the map silently stops
    // panning for the rest of the mount. These fire after the node handlers
    // (bubble phase), so a normal release still commits exactly once —
    // finishPointer is idempotent because it re-checks the pointer id.
    const win = typeof window !== 'undefined' ? window : null
    win?.addEventListener('pointerup', onPointerUp)
    win?.addEventListener('pointercancel', onPointerCancel)
    return () => {
      node.removeEventListener('pointerdown', onPointerDown)
      node.removeEventListener('pointermove', onPointerMove)
      node.removeEventListener('pointerup', onPointerUp)
      node.removeEventListener('pointercancel', onPointerCancel)
      node.removeEventListener('click', onClickCapture, true)
      node.removeEventListener('touchmove', onTouchMove)
      win?.removeEventListener('pointerup', onPointerUp)
      win?.removeEventListener('pointercancel', onPointerCancel)
      // The deps below change on resize/re-measure, which can tear the
      // listeners down mid-gesture. Clear the in-flight pointer so the
      // re-attached listeners start from a clean slate instead of inheriting a
      // pointer id that can no longer be completed.
      if (webPanPointerId.current !== null) release(webPanPointerId.current)
      webPanPointerId.current = null
      webPanMoved.current = false
      webSuppressClick.current = false
    }
  }, [zoomEnabled, containerWidth, mapX, mapY, mapW, mapH])

  // Production currently has no useful client render transactions. Keep a
  // low-volume, slow-commit marker so real devices can tell us whether a map
  // or its surrounding game screen is the expensive part. No game content is
  // recorded: only map id, device model, render phase and duration. The
  // sample decision is per mounted map, and the cooldown prevents a busy
  // session from generating a stream of diagnostic events.
  const mapRenderSampledRef = React.useRef(shouldSampleMapRender())
  const lastMapRenderReportRef = React.useRef(0)
  const onMapRender = React.useCallback<React.ProfilerOnRenderCallback>(
    (_id, phase, actualDuration) => {
      if (!shouldReportMapRender(actualDuration, mapRenderSampledRef.current)) return
      const now = Date.now()
      if (now - lastMapRenderReportRef.current < MAP_RENDER_REPORT_COOLDOWN_MS) return
      lastMapRenderReportRef.current = now
      const device = collectDeviceInfo()
      reportMessage('slow-map-render', {
        level: 'info',
        tags: { mapId: state.map.id, platform: String(device.platform ?? 'unknown') },
        extra: {
          phase,
          actualDurationMs: Math.round(actualDuration * 10) / 10,
          deviceModel: device.deviceModel ?? 'unknown',
        },
      })
    },
    [state.map.id],
  )

  // The map key lines up with the drawn map's outer edges (its territories),
  // not the wrapper's corner.
  const legendInsets = React.useMemo(() => {
    if (!state.map.legend) return null
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const t of geomTerritories) {
      for (const [x, y] of parsePolygonVertices(t.shape)) {
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
    if (!isFinite(minX)) return { top: 8, right: 8, bottom: 8, left: 8 }
    return mapContentInsets(
      { minX, minY, maxX, maxY },
      { x: mapX, y: mapY, width: mapW, height: mapH },
      { width: svgWidth, height: svgHeight, left: (containerWidth - svgWidth) / 2 },
      containerWidth,
    )
  }, [state.map.legend, geomTerritories, mapX, mapY, mapW, mapH, svgWidth, svgHeight, containerWidth])

  // Shared by the board and the overlay layers stacked over it.
  const boardViewBox = `${mapX} ${mapY} ${mapW} ${mapH}`
  const boardTransform = zoomEnabled ? panTransform(activeTx, activeTy, effectiveZoomScale) : undefined
  const overlayLeft = (containerWidth - svgWidth) / 2
  const transientFrame = React.useMemo<MapOverlayFrame>(() => ({
    left: overlayLeft,
    width: svgWidth,
    height: svgHeight,
    viewBox: boardViewBox,
    transform: boardTransform,
    groupRef: registerTransientGroup,
  }), [overlayLeft, svgWidth, svgHeight, boardViewBox, boardTransform, registerTransientGroup])
  // Overlay <Svg>s get their own id prefixes: on web every SVG id shares the
  // document, so reusing the board's would bind to the board's definitions.
  const selectionClipNS = `${clipNS}sel-`
  const effectsClipNS = `${clipNS}fx-`
  const overlayClipDefs = (ids: readonly (string | undefined)[], scope: 'sel-' | 'fx-') =>
    [...new Set(ids)].map((id) => {
      const shape = id ? territoryShapeById[id] : undefined
      return id && shape ? (
        <ClipPath key={`clip-${id}`} id={`${clipNS}${scope}clip-${id}`}>
          <Path d={shape} fillRule={evenOdd} />
        </ClipPath>
      ) : null
    })

  // Attack/placement/fortify animations. On circle-marker boards they draw in their
  // own overlay ABOVE the circles, so nothing is hidden under a disc; everywhere
  // else they stay under the troop numbers. The attack "−N" labels always draw
  // above the numbers (attackLabelLayers).
  const markerMap = !!state.map.territoryMarkers
  const badgeBounds = React.useMemo(() => {
    const vb = state.map.viewBox
    return { x: vb.x ?? 0, y: vb.y ?? 0, width: vb.width, height: vb.height }
  }, [state.map.viewBox])
  const animationLayers = (
    <MapAnimationLayers
      events={events}
      slotByPlayerId={colorByPlayerId}
      playerColorHex={playerColorHex}
      viewerJoinedAt={viewerJoinedAt}
      shapeById={shapeById}
      positionById={positionById}
      badgePositionById={badgePositionById}
      badgeRadius={badgeRadius}
      conquestFlashStrokeWidth={conquestFlashStrokeWidth}
      badgeFontSize={badgeFontSize}
      attackBadgeFontSize={attackBadgeFontSize}
      fortifyTrailWidth={validStrokeWidth}
      staticConquestRing={staticConquestRing}
      staticAttackBadge={staticAttackBadge}
      staticFortifyEntry={staticFortifyEntry}
      badgeBounds={badgeBounds}
    />
  )
  const attackLabelLayers = (
    <AttackLabelLayers
      events={events}
      slotByPlayerId={colorByPlayerId}
      playerColorHex={playerColorHex}
      viewerJoinedAt={viewerJoinedAt}
      positionById={positionById}
      attackBadgeFontSize={attackBadgeFontSize}
      staticAttackBadge={staticAttackBadge}
      badgeBounds={badgeBounds}
    />
  )

  return (
    <React.Profiler id={`map-${state.map.id}`} onRender={onMapRender}>
      <View
        ref={wrapperRef as never}
        {...(Platform.OS !== 'web' && zoomEnabled ? panResponder.panHandlers : {})}
        style={{
        // Wrapper extends the ocean color across the map container
        // width so the map visually fills the available pane even
        // when the SVG itself is letterboxed (e.g. landscape with a
        // wide-aspect world map, or landscape with the sidebar
        // narrowing the container). The SVG is centered inside.
        width: containerWidth,
        height: svgHeight,
        backgroundColor: palette.ocean,
        alignItems: 'center',
        justifyContent: 'center',
        // The pan now translates the inner view rather than the SVG's contents,
        // so without this a panned board would paint over whatever sits next to
        // the map (the phase UI below it, the sidebar beside it). Clipping here
        // restores the boundary the SVG viewport used to provide for free.
        overflow: 'hidden',
        // Touch-gesture ownership (web only):
        //   • touch-action: none (when pannable) — the map drag must own
        //     the whole touch gesture. `none` stops the browser from
        //     panning/scrolling the page (or an ancestor ScrollView, e.g.
        //     the portrait map-detail screen where the map sits above a
        //     scrollable region list) when the user drags inside the map,
        //     AND suppresses Safari's native pinch-zoom that otherwise
        //     fights the map gesture. When the map isn't pannable
        //     (zoom disabled) we leave normal scrolling intact so a
        //     non-interactive map inside a scroll view doesn't trap the
        //     page.
        //   • userSelect: none — stops text-selection sweeps from
        //     triggering Safari's elastic pan on the outer page.
        // These properties are valid on RN Web but do not exist in
        // the RN native StyleSheet spec, hence the `as any` casts.
        ...(Platform.OS === 'web'
          ? ({
              touchAction: zoomEnabled ? 'none' : 'pan-x pan-y',
              userSelect: 'none',
            } as any) // eslint-disable-line @typescript-eslint/no-explicit-any
          : undefined),
        }}
      >
        <Svg
          width={svgWidth}
          height={svgHeight}
          viewBox={boardViewBox}
        >
        {/* ClipPath defs — one per territory, one per region.
            Every id is namespaced with `clipNS` (see the clipNS note in
            MapViewInner): these <ClipPath> ids live in the GLOBAL SVG id
            space, and screens routinely mount more than one MapView at once
            (portrait/landscape, phase UIs, the historical view beside the
            live board). Without the per-instance prefix, two instances of the
            same map emit duplicate `clip-region-<id>` ids; on web the browser
            then binds `url(#clip-region-…)` to a sibling instance's clip,
            leaving the region perimeter UNCLIPPED (full-width stroke spilling
            past every coastline → thick/fuzzy borders) until a full page
            reload collapses the tree. The clipNS prefix makes each instance's
            clips unique so references resolve correctly on the first paint.

            PLACEMENT: <Defs> is a direct child of <Svg>, before the zoom/pan
            <G>. clipPathUnits defaults to "userSpaceOnUse", so the clip
            coordinates are interpreted in the referencing element's coordinate
            system at apply time — the same scaled/translated space the polygon
            paths use. The polygon and its clip carry identical `d` values, so
            they align regardless of where <Defs> sits in the tree.

            - Territory clips confine each polygon's stroke to its own
              interior so two adjacent same-region cells sum to a full-
              width internal border (each side shows half).
            - Region clips confine the region perimeter overlay to the
              region's own interior, so only the inner half of that
              stroke is visible; the outer half is cut. */}
        <Defs>
          {clipDefs}
          {/* Very light 45° cross-hatch — overlaid on border (adjacent)
              territories as a subtle texture. Two perpendicular line sets
              rotated 45° = a diagonal grid. */}
          <Pattern
            id={`${clipNS}adj-hatch`}
            patternUnits="userSpaceOnUse"
            width={hatchGap}
            height={hatchGap}
            patternTransform="rotate(45)"
          >
            <Line x1={0} y1={0} x2={0} y2={hatchGap} stroke="#ffffff" strokeWidth={hatchGap * 0.16} strokeOpacity={0.28} />
            <Line x1={0} y1={0} x2={hatchGap} y2={0} stroke="#ffffff" strokeWidth={hatchGap * 0.16} strokeOpacity={0.28} />
          </Pattern>

          {/* Fog of war (concept B2): a fine olive crosshatch over territories
              outside the viewer's vision, in the theme's border colour so it
              reads on light and dark boards alike. */}
          {foggedIds.size > 0 && <FogHatchPattern id={`${clipNS}fog-hatch`} gap={hatchGap * 0.45} background={HEAVY_FOG_FILL} color={HEAVY_FOG_LINE} />}

          {/* One-way reach: the SAME light cross-hatch as a two-way neighbour,
              in light purple instead of white. Identical geometry and opacity
              on purpose — the colour alone carries "you can attack there and it
              cannot answer", so the two read as the same kind of information.
              Geometry is shared below so the pair cannot drift apart. */}
          <Pattern
            id={`${clipNS}oneway-hatch`}
            patternUnits="userSpaceOnUse"
            width={hatchGap}
            height={hatchGap}
            patternTransform="rotate(45)"
          >
            <Line x1={0} y1={0} x2={0} y2={hatchGap} stroke="#d8b4fe" strokeWidth={hatchGap * 0.16} strokeOpacity={0.55} />
            <Line x1={0} y1={0} x2={hatchGap} y2={0} stroke="#d8b4fe" strokeWidth={hatchGap * 0.16} strokeOpacity={0.55} />
          </Pattern>

        </Defs>

        {/* Zoom-and-pan transform group.
            Applies translate(tx, ty) then scale(zoomScale) around the viewBox
            origin, so the SVG viewport clips the panned content. During a DRAG
            this same transform is pushed straight to the native view instead
            (see "Where the pan lives") — the value below is what every other
            path renders, and what the drag hands back on release.
            When zoom is off the transform is a no-op (no extra nodes needed;
            the <G> is always rendered for simplicity). */}
        <G
          ref={zoomGroupRef}
          transform={boardTransform}
        >

        {/* Background tap target — an ocean-filled rect behind everything that
            catches taps on any non-territory area (ocean, continent gaps) and
            clears the selection. Territory <G>s render on top with their own
            onPress, so they still capture their taps; the continent fills below
            are pointerEvents:none so their area falls through to this rect.
            Sized well beyond the viewBox so it still covers when panned/zoomed.
            Only mounted when a handler is supplied (other MapView usages skip). */}
        {onBackgroundPress && (
          <Rect
            x={mapX - mapW}
            y={mapY - mapH}
            width={mapW * 3}
            height={mapH * 3}
            fill={palette.ocean}
            onPress={onBackgroundPress}
          />
        )}

        {/* Raster backdrop (MapDef.backgroundImage) — for hand-drawn/isometric
            maps whose art is a bitmap and whose territories are invisible
            hit-regions on top. Fills the map viewBox exactly (art is authored
            at viewBox resolution), sits under the territory polygons, and
            pans/zooms with the board. pointerEvents:none so taps fall through
            to territories / the background rect. */}
        {state.map.backgroundImage && MAP_BACKGROUND_IMAGES[state.map.backgroundImage] && (
          <SvgImage
            x={artX}
            y={artY}
            width={artW}
            height={artH}
            href={MAP_BACKGROUND_IMAGES[state.map.backgroundImage]}
            preserveAspectRatio="none"
            pointerEvents="none"
          />
        )}

        {/* Continent backgrounds — drawn beneath everything. Each region's
            shape (if defined) gets a soft tan fill that "shows through" any
            transparency in territory fills. Suppressed entirely when the
            map sets `noContinentFill: true` (e.g. Forbidden City, where the
            tan backdrop reads as visual clutter behind the building cells —
            the ocean colour shows through instead). pointerEvents:none so taps
            on the continent (but not a territory) fall through to the
            background rect above (selection-clearing). */}
        {continentFillLayer}

        {/* Decorative non-territory features (lakes, inland seas).
            Rendered after the continent fill so the colour shows even
            over a region.shape, and before the territory polygons so
            the cells still paint on top of any incidental overlap. */}
        <MapDecorations decorations={state.map.decorations} onLogoPress={openAbout} />

        {/* Border-bridge underlay — each polygon stroked with the
            border colour, NOT clipped, so the stroke extends outward
            by ½ width and paints the inter-cell gap. Adjacent cells'
            extensions meet in the middle of the gap; cells with no
            gap simply cover this layer with their normal fill. Off
            (no render) when bridgeStrokeWidth is 0 / undefined. */}
        {bridgeLayer}

        {/* Territory polygons. Each is its own <G> so Playwright + a11y get
            a single targetable node per territory. The base layer keeps the
            map's canonical order; the selected/secondary visual overlays below
            preserve highlight z-order without sorting the whole SVG subtree. */}
        {(() => {
          const renderTerritory = (
            territory: (typeof state.map.territories)[number],
            interactive: boolean,
            keyPrefix: string,
          ) => {
          const isSelected = territory.id === selectedTerritoryId
          const isSecondary =
            secondaryTerritoryId !== undefined &&
            territory.id === secondaryTerritoryId &&
            !isSelected
          const isInSelectedRegion =
            selectedRegionId !== undefined && territory.regionId === selectedRegionId
          const ownerColor = ownerFill(territory.ownerId, colorByPlayerId, palette.unowned)
          // Region-bonus overlay: ignore ownership entirely and fill
          // every territory with a lightened version of its region
          // colour. The cross-region perimeter overlay below already
          // supplies the saturated region border, so a soft fill +
          // strong border together read as "this is the region" without
          // any per-territory clutter.
          //
          // On a BACKDROP map that 55% white is most of why regions looked
          // washed out: mixing it in drops every pinned colour to 5–39%
          // saturation, and what's left then blends with grey structure art.
          // A vector map needs the tint pale (nothing underneath competes with
          // it); raster art needs the opposite. So backdrop maps keep far more
          // of the author's colour — that, not opacity alone, is what makes a
          // fort read as ITS colour instead of a grey smudge.
          const backdropRegionTint =
            !!state.map.backgroundImage && !!territory.regionId
          // Is this cell part of the region the user is looking at? Selection can
          // arrive as either prop — the in-game regions view passes both — so both
          // count, and this one boolean drives the fill, the opacity and nothing
          // else, so the three cannot disagree about what "emphasised" means.
          const regionIsEmphasised =
            isInSelectedRegion || isSelectedRegionId(territory.regionId)
          const rawRegionColor = regionColorById[territory.regionId ?? ''] ?? palette.unowned
          const regionTintFill = showRegionBonuses
            ? // The emphasised region on a backdrop map gets an ILLUMINATED version
              // of its own colour — same hue, lifted to a brightness that reads as
              // lit. This is the fix for "selecting the region doesn't light the
              // fort up": the resting tint is the raw pin plus 18% white, and six of
              // Capture the Flag's sixteen pins sit at 18–31% lightness, so the
              // "selected" fill on those was a near-black smudge no matter what
              // opacity it was painted at. Amber cut through instantly for exactly
              // that reason — and discarded region identity, which is why the fill
              // has to stay the region's own hue instead.
              backdropRegionTint && regionIsEmphasised
              ? illuminateHex(rawRegionColor)
              : lightenHex(rawRegionColor, backdropRegionTint ? BACKDROP_REGION_TINT_LIGHTEN : 0.55)
            : null
          // Fill priority: inspector selection > region-highlight > owner.
          // Live source and destination keep their original owner fill;
          // the inner halo carries their selection state.
          // Adjacent/bordering territories keep their OWN fill and are marked
          // by a light 45° hatch overlay (rendered in a later layer) instead of
          // being recoloured — so you can still read their region/owner colour.
          const fill = state.map.territoryMarkers && !showRegionBonuses && !(isSelected && selectedFillMode === 'amber')
            ? rawRegionColor
            : isSelected
            ? selectedFillMode === 'lighten'
              ? ownerColor
              : SELECTED_FILL
            : isSecondary && selectedFillMode === 'lighten'
              ? ownerColor
              : // `selectedRegionColor` arrives pre-lightened by the caller, so
                // honouring it on a backdrop map would make the SELECTED region
                // paler than its unselected neighbours — backwards. Selection is
                // already carried there by the outer glow, the inner contrast
                // outline, and the spotlight dim over everything else, so the
                // saturated tint wins.
                backdropRegionTint && showRegionBonuses && regionTintFill
                ? regionTintFill
                : isInSelectedRegion && selectedRegionColor
                  ? selectedRegionColor
                  : regionTintFill ?? ownerColor
          // Stroke priority: valid target > selected > inside-selected-region
          // > region-highlight > default border. When a region is selected
          // and its territories all share `selectedRegionColor`, the
          // *internal* borders use the contrast colour of that fill
          // (dark border on light fill or vice versa) so individual
          // territories remain readable instead of melting into one blob.
          // Default stroke = the territory's region colour (from the
          // explicit highlightRegions map if the caller passed one, or
          // the DEFAULT_REGION_PALETTE fallback otherwise). At a shared
          // edge the neighbour territory paints its own region colour
          // on the other side via clipPath, yielding a two-toned border.
          // Stroke colour follows the SAME rules whether selected or
          // not — selection is signalled by the inner glow below.
          // The amber inspector case (selectedFillMode='amber') keeps
          // a contrast stroke so the outline stays visible against
          // its bright fill.
          const backdropTerritoryStroke =
            state.map.backgroundImage && territory.regionId
              ? BACKDROP_TERRITORY_STROKE_OVERRIDES[territory.regionId] ??
                regionColorById[territory.regionId]
              : undefined
          const strokeColor =
            // In regions view the selected territory is marked by the star — it
            // does NOT get the extra amber contrast border (it just uses the
            // normal region-consistent stroke like its siblings).
            isSelected && selectedFillMode === 'amber' && !showRegionBonuses
              ? contrastTextColor(SELECTED_FILL)
              : isInSelectedRegion && selectedRegionColor
                ? contrastTextColor(selectedRegionColor)
                : backdropTerritoryStroke ?? regionColorById[territory.regionId ?? ''] ??
                  (state.map.backgroundImage
                    ? 'rgba(30,30,30,0.4)' // fallback only when a territory has no region
                    : palette.border)
          // Stroke width = `internalBorderWidth` for all polygons,
          // clipped to each polygon's own interior. Adjacent same-
          // region cells together show the full width on their shared
          // edge. Region perimeter overlay below paints on top of this
          // for outer-region edges. Inspector (`amber`) selection and
          // the selected-region inset use bumped widths.
          const strokeWidth =
            isSelected && selectedFillMode === 'amber' && !showRegionBonuses
              ? selectedStrokeWidth
              : isInSelectedRegion
                ? internalBorderWidth * 1.6
                : territoryBorderWidth // thin over raster art unless regionColoredInternalBorders
          // Backdrop maps draw every fill semi-transparent so the raster
          // artwork reads through it. That is right for ownership tints during
          // play, but it also muted the thing selection is supposed to shout:
          // a selected region's tint sat at 0.55 over dark art and barely lit
          // up, where a vector map shows the same tint at full strength. Give
          // the emphasised cells — the selection, a fortify target, and every
          // territory of the focused region — most of that opacity back, and
          // leave the rest at 0.55 so the art still carries the board.
          const backdropFillOpacity = resolveBackdropFillOpacity({
            hasBackgroundImage: !!state.map.backgroundImage,
            opaqueTerritoryFills: state.map.opaqueTerritoryFills,
            showRegionBonuses,
            isSelected: isSelected && selectedFillMode === 'amber',
            isSecondary: isSecondary && selectedFillMode === 'amber',
            regionIsEmphasised,
            hasRegion: !!territory.regionId,
            emphasisOpacity: BACKDROP_EMPHASIS_FILL_OPACITY,
            regionOpacity: BACKDROP_REGION_FILL_OPACITY,
            restingOpacity: BACKDROP_FILL_OPACITY,
          })
          // Territory polygon clipPath confines the stroke to the polygon's
          // interior so shared edges show each side's region colour (see Defs
          // block). Selected territories use the same clipped border as
          // everyone else; the selection inner line is a SEPARATE layer below,
          // after the coastal + region-perimeter overlays. The per-territory
          // node is a memoized <TerritoryShape> so only changed territories
          // reconcile on selection / owner / armies / animation changes.
          return (
            <TerritoryShape
              key={`${keyPrefix}-${territory.id}`}
              territoryId={territory.id}
              clipNS={clipNS}
              shape={territory.shape}
              positionX={territory.position.x}
              positionY={territory.position.y}
              circleRadius={badgeRadius * 1.5}
              // Fog of war, heavy fog only (owner unknown): the fog pattern IS
              // the fill (light grey + tight diagonals), so this shape's border
              // and the region lines paint over it. Light fog keeps the owner's
              // colours as normal and only drops the troop number. Marker maps
              // (Warfish style) fog only the disc and keep the land visible.
              // The Regions view shows the map's layout, which isn't secret,
              // so it keeps region colours.
              fill={territory.fogged && !territory.ownerId && !state.map.territoryMarkers && !showRegionBonuses ? `url(#${clipNS}fog-hatch)` : fill}
              fillOpacity={backdropFillOpacity}
              fillRule={state.map.backgroundImage ? 'evenodd' : undefined}
              stroke={state.map.territoryMarkers ? '#242424' : strokeColor}
              strokeWidth={strokeWidth}
              interactive={interactive}
              accessibilityLabel={territory.fogged
                ? `${territory.name}, hidden by fog${territory.ownerId ? `, owned by player ${slotByPlayerId[territory.ownerId] ?? '?'}` : ''}`
                : `${territory.name}, ${territory.armies} armies${
                  territory.ownerId ? `, owned by player ${slotByPlayerId[territory.ownerId] ?? '?'}` : ', unowned'
                }`}
              onPress={interactive && onTerritoryPress ? handleTerritoryPress : undefined}
              // Web pointer cursor on the currently-actionable territories
              // (showsPointer). Signals clickability on desktop hover.
              pointer={interactive && showsPointer(territory.id) ? true : undefined}
            />
          )
          }

          const secondary = secondaryTerritoryId && secondaryTerritoryId !== selectedTerritoryId
            ? state.map.territories.find((territory) => territory.id === secondaryTerritoryId)
            : undefined
          const selected = selectedTerritoryId
            ? state.map.territories.find((territory) => territory.id === selectedTerritoryId)
            : undefined

          return (
            <>
              {state.map.territories.map((territory) =>
                renderTerritory(territory, true, 'territory'),
              )}
              {secondary && selectedFillMode === 'amber' && renderTerritory(secondary, false, 'secondary-overlay')}
              {/* Region/explorer mode already paints the selected cell in the
                  canonical layer. Avoid a duplicate paint-only path there:
                  on Fabric/iOS a transparent overlay can still absorb the
                  second tap even with pointerEvents="none", preventing a
                  selected territory from being toggled off. Live selection
                  uses halos only: repainting a translucent fill would change
                  its color on raster maps. */}
              {selected && selectedFillMode === 'amber' && !showRegionBonuses && renderTerritory(selected, false, 'selected-overlay')}
            </>
          )
        })()}

        {/* Traced-border overlay — the source bitmap's actual black-border
            network, extracted as an SVG path (see
            scripts/maps/extract-border-path.py). Vector maps use it for
            their visible cell borders. Raster-backed maps already carry
            their border artwork and use region-coloured territory strokes;
            painting this dark path over them would erase that palette. */}
        {state.map.borderShape && !state.map.backgroundImage && (
          <Path
            d={state.map.borderShape}
            fill="none"
            stroke={palette.border}
            strokeWidth={Math.max(1, sizingWidth * 0.002)}
            strokeLinejoin="round"
            strokeLinecap="round"
            pointerEvents="none"
          />
        )}

        {/* Selected-region inner outline — a contrast line (black on light
            region fills, white on dark) around the FULL region perimeter, just
            inside the region-colour border, so the whole selected region is
            outlined to match its black internal territory borders. Drawn as a
            wider band clipped to the region interior, UNDER the region-perimeter
            overlay below — the region colour then paints over its outer edge,
            leaving the contrast line showing just inside the border. */}
        {state.map.regions.map((region) => {
          const isSel =
            (region.id === selectedRegionId || region.id === regionFocusId) && !!region.shape
          if (!isSel) return null
          const rc =
            (region.id === selectedRegionId && selectedRegionColor) ||
            regionColorById[region.id] ||
            palette.border
          return (
            <Path
              key={`region-inner-outline-${region.id}`}
              d={region.shape!}
              fill="none"
              stroke={contrastTextColor(rc)}
              strokeWidth={regionBorderWidth * 1.66}
              strokeLinejoin="round"
              strokeLinecap="round"
              clipPath={`url(#${clipNS}clip-region-${region.id})`}
              pointerEvents="none"
            />
          )
        })}

        {/* Region perimeter overlay — paints each region's union
            shape stroked at `regionBorderWidth`, clipped to the
            region's interior. Only the inner half of the stroke is
            visible. At a single-region perimeter the cell side already
            paints internalBorderWidth/2 inward, so the visible total
            is (internalBorderWidth + regionBorderWidth) / 2. At a
            cross-region edge both regions paint inward, doubling that.
            Coastal edges work the same as single-region perimeters —
            no neighbouring region, no separate coastal layer needed. */}
        {state.map.regions.map((region) => {
          if (!region.shape) return null
          // Every region draws its own-colour perimeter. The selected region is
          // NOT emphasised with a bumped white/contrast inner border any more
          // (that covered the territory borders inside it) — it's marked by the
          // outer glow layer below instead.
          const baseStroke = state.map.territoryMarkers ? '#242424' : regionColorById[region.id] ?? palette.border
          // The selected region's outer line is a touch thinner so the inner
          // contrast outline just inside it reads clearly.
          const isSel = region.id === selectedRegionId || region.id === regionFocusId
          return (
            <Path
              key={`region-border-${region.id}`}
              d={region.shape}
              fill="none"
              stroke={baseStroke}
              strokeWidth={isSel ? regionBorderWidth * 0.72 : regionBorderWidth}
              strokeLinejoin="round"
              strokeLinecap="round"
              clipPath={`url(#${clipNS}clip-region-${region.id})`}
              pointerEvents="none"
            />
          )
        })}

        {/* Spotlight dim — fade every non-bright territory (not the selected
            region, the selection, or a bordering territory) toward the ocean
            colour. Rendered after the fills + borders so it dims both; the
            selected region's glow + bonus badges render after and stay bright.

            On a map with a walls overlay this renders LATER instead (see
            `dimAboveWalls`) — the overlay is a full-board raster of the walls
            and buildings drawn last, so a dim underneath it left every
            structure at identical full brightness no matter which region was
            selected. Selecting a fort could not make its own fort stand out. */}
        {!dimAboveWalls && spotlightDimLayer}


        {/* Border-territory cross-hatch — a very light diagonal grid on each
            bordering (adjacent) territory as a subtle "this borders your
            selection" texture. */}
        {geomTerritories.map((territory) =>
          territory.shape && adjacentSet.has(territory.id) && territory.id !== selectedTerritoryId ? (
            <Path
              key={`adj-hatch-${territory.id}`}
              d={territory.shape}
              fill={`url(#${clipNS}adj-hatch)`}
              pointerEvents="none"
            />
          ) : null,
        )}

        {/* One-way reach of the selection — a light wash with the same
            cross-hatch a two-way neighbour gets, in purple rather than white.
            Arrows were tried first and buried the board in 69 lines.

            REGIONS VIEW ONLY. This layer explains the board's topology, which
            is what that view is for. During play it fought the thing you were
            actually doing: tapping a tower to attack lit up its whole one-way
            reach — a dozen washed cells — when the phase already marks the
            legal targets via validTargetIds. Selecting a unit should show the
            unit selected, not pre-empt the choice you are about to make. */}
        {showRegionBonuses &&
          geomTerritories.map((territory) =>
          territory.shape &&
          oneWaySet.has(territory.id) &&
          territory.id !== selectedTerritoryId ? (
            <G key={`oneway-${territory.id}`} pointerEvents="none">
              {/* Light wash first: it lifts the cell off the grey art and gives
                  the purple hatch something to read against. Without it the
                  purple sits on mid-grey and goes muddy. */}
              <Path d={territory.shape} fill="#ffffff" fillOpacity={0.38} />
              <Path d={territory.shape} fill={`url(#${clipNS}oneway-hatch)`} />
            </G>
          ) : null,
        )}

        {/* Selected-region OUTER glow — a soft white bloom hugging the OUTSIDE
            of the region's perimeter, replacing the old solid white inner
            border. Layered semi-transparent strokes (widest+faintest →
            narrowest+strongest) read as a gradient glow; each is clipped to the
            region's EXTERIOR (a full-viewBox rect with the region punched out
            via evenodd) so the glow only blooms outward and never tints the
            territories inside. Shows over neighbouring regions since it's drawn
            on top. Suppressed once a specific territory is selected — the
            spotlight dim already carries the emphasis there. */}
        {!selectedTerritoryId &&
          state.map.regions.map((region) => {
          const isSel =
            (region.id === selectedRegionId || region.id === regionFocusId) && !!region.shape
          if (!isSel) return null
          const rect = `M${mapX} ${mapY}H${mapX + mapW}V${mapY + mapH}H${mapX}Z`
          // Glow in the region's OWN (raw, saturated) colour. Prefer
          // regionColorById over selectedRegionColor: the latter is often a
          // LIGHTENED fill tint (map explorer / map-details), which made the
          // glow pale/near-invisible — the in-game regions view passed the raw
          // colour and glowed vividly. Using the raw colour matches that.
          const glowColor =
            regionColorById[region.id] ||
            (region.id === selectedRegionId ? selectedRegionColor : undefined) ||
            palette.border
          return (
            <G key={`region-glow-${region.id}`} pointerEvents="none">
              <Defs>
                <ClipPath id={`${clipNS}outer-region-${region.id}`}>
                  <Path d={`${rect} ${region.shape}`} clipRule="evenodd" />
                </ClipPath>
              </Defs>
              {/* Many overlapping low-opacity passes (widest → narrowest, all
                  clipped to the exterior) blend into a smooth gradient falloff
                  instead of the few visible bands the 3-pass version showed. */}
              {Array.from({ length: 14 }, (_, i) => {
                const t = i / 13 // 0 = widest/outermost, 1 = narrowest/innermost
                const mult = 5.25 * (1 - t) + 0.5 * t // 25% narrower than before
                return (
                  <Path
                    key={i}
                    d={region.shape!}
                    fill="none"
                    stroke={glowColor}
                    strokeOpacity={0.06}
                    strokeWidth={regionBorderWidth * mult}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    clipPath={`url(#${clipNS}outer-region-${region.id})`}
                  />
                )
              })}
            </G>
          )
        })}

        {/* Sea-route connector lines for non-contiguous adjacencies. Each
            line spans the visual gap between two polygons — anchored to
            the nearest vertex of each, not the badge centres — so it
            reads as a sea/strait crossing rather than a label
            connector. Dashed sky-blue is deliberately not any owner /
            region colour.
            Suppressed entirely when the map sets hideAdjacencyLines=true
            (e.g. Forbidden City, whose road network already conveys
            connectivity — the connector lines would be visual clutter).
            Forbidden City is also matched by id so that in-flight games
            whose persisted state.map was snapshotted before the flag
            was added still hide the lines. */}
        {state.map.territoryMarkers ? !showRegionBonuses && !hideBadges && <MarkerConnectionsLayer territories={geomTerritories}
          radius={territoryMarkerMetrics(state.map.territoryMarkers.radius, isLandscape, desktop).radius} /> : seaRouteLayer}

        {/* Border chevrons (MapDef.borderChevrons): one red chevron centred on
            each border where attacking in rolls a better die (Royal Rumble's
            Sand). Above the territory fills, below the troop badges. */}
        {borderChevronLayer}


        {/* Troop-count badges — text-only over the territory fill, with
            colour chosen for WCAG contrast against the fill and a thin
            outline of the opposite colour so the number reads against
            partially-transparent fills, hatching, or anything mid-tone.
            Territories whose polygon area is too small to fit a 2-digit
            number are silently skipped (extremely-tiny islands).
            Hidden entirely when `hideBadges` is set (Map Details preview
            mode where every territory is unowned with 0 armies). Also
            hidden when `showRegionBonuses` is on — the region-bonus
            overlay reads as a pure region-structure view, troop counts
            and ownership colours both step out. */}
        {/* Walls overlay (MapDef.wallsOverlay) — redraws the walls ON TOP of the
            territory tints so no territory colour can bleed onto a wall. Above
            all territory/region layers, below the troop badges. */}
        {state.map.wallsOverlay && MAP_BACKGROUND_IMAGES[state.map.wallsOverlay] && (
          <SvgImage
            x={artX}
            y={artY}
            width={artW}
            height={artH}
            href={MAP_BACKGROUND_IMAGES[state.map.wallsOverlay]}
            preserveAspectRatio="none"
            pointerEvents="none"
          />
        )}

        {/* Spotlight dim, above the walls overlay — so the structures dim with
            the ground they stand on and the selected region's building is the
            only one left at full brightness.

            Only the DIM moves up here, never the territory fills. Moving the
            overlay itself down was tried and looked wrong: the fills then
            painted over the buildings, turning a fort into a flat pink
            silhouette instead of tinting the ground around it. Fills stay
            below the art; only the shading sits above it. */}
        {dimAboveWalls && spotlightDimLayer}


        {/* Structure re-light — the selected region's own colour, translucent,
            over the walls raster. The ONE exception to "fills stay below the
            art", and it exists because the overlay is opaque over a building:
            a lit flag cell reached its full chip colour while a lit fort stayed
            a dark lump, because half of that keep is opaque line-art the tint
            could never reach. Translucent, so the stonework still reads through.
            Layer ORDER is untouched — this is an addition above the raster, not a
            reordering of it. See `structureRelightLayer`. */}
        {structureRelightLayer}

        {/* Contour restore — the walls raster again, translucent, clipped to the
            re-lit region. The re-light above lifts the building's near-black
            OUTLINES along with its stonework, which left a fort reading as a flat
            coloured shape; drawing the art back over it darkens what was darkest
            and barely touches the rest, so the drum, roofline and pole return.
            Same image, same rect, same aspect ratio as the overlay above — it is
            the same layer painted twice, not a reordering. */}
        {structureRelightLayer && state.map.wallsOverlay &&
          MAP_BACKGROUND_IMAGES[state.map.wallsOverlay] &&
          state.map.regions.map((region) =>
            region.shape && isSelectedRegionId(region.id) ? (
              <SvgImage
                key={`contours-${region.id}`}
                x={artX}
                y={artY}
                width={artW}
                height={artH}
                href={MAP_BACKGROUND_IMAGES[state.map.wallsOverlay!]}
                preserveAspectRatio="none"
                opacity={STRUCTURE_CONTOUR_OPACITY}
                clipPath={`url(#${clipNS}clip-region-${region.id})`}
                pointerEvents="none"
              />
            ) : null,
          )}


        {/* NOTHING is drawn over the selected region's cells here — deliberately.
            A white "illuminate" wash was tried and made things worse: the walls
            raster carries the crisp dark line-art of the towers and flags, and
            any veil over it mutes exactly the detail that makes a structure
            read. The selection pops because its own fill is vivid and the rest
            of the board is dimmed, not because something is layered on top. */}

        {/* Region-bonus chips sit ABOVE the walls overlay. They used to be
            drawn before it, so on a backdrop map the raster walls painted
            straight over them — a chip on a wall or a structure was half
            swallowed. Troop badges were already above the overlay; these
            belong in the same band.
        {/* Region bonus badge overlay — filled circles at each region's
            interior anchor, labelled with the reinforcement bonus.
            Rendered ABOVE territory polygons and region outlines but
            BELOW troop-count badges so army counts remain dominant.
            Only visible when showRegionBonuses is true.
            Badge radius scales with sizingWidth so it looks proportional
            on all maps (e.g. 25px on a 1000-px-wide map) and stays a
            constant size across orientations. The focused
            region (regionFocusId) gets a 30% larger radius + thicker
            stroke so it pops. */}
        {showRegionBonuses && regionBonusAnchors && state.map.regions.map((region) => {
          const anchor = regionBonusAnchors[region.id]
          if (!anchor) return null
          const baseColor = regionColorById[region.id] ?? palette.border
          // Non-selected chips read as de-emphasised by blending their fill
          // toward the ocean/background — a fully-OPAQUE colour (not a
          // see-through opacity), so the chip stays a solid disc and no
          // territory border bleeds through it, and it recedes like the dimmed
          // territories under it. Selected/focused chip keeps its full colour.
          const faded = spotlightActive && !isSelectedRegionId(region.id)
          // The emphasised chip is lit the same way its territories are, so the
          // chip and the ground it labels agree about what colour the region is.
          // Without this a dark pin gave a near-black chip sitting on top of a
          // brightly-lit region — reading as two different regions.
          const lit = !faded && !!state.map.backgroundImage && isSelectedRegionId(region.id)
          const color = faded
            ? mixHex(baseColor, spotlightDim, 0.7)
            : lit
              ? illuminateHex(baseColor)
              : baseColor
          const isFocused = regionFocusId === region.id
          const r = Math.max(3, sizingWidth * 0.025) * (isFocused ? 1.15 : 1)
          const strokeW = isFocused ? Math.max(1, sizingWidth * 0.003) : Math.max(0.5, sizingWidth * 0.0015)
          // Text contrasts against the badge fill; outline halo is the
          // OPPOSITE of the text so the digit reads cleanly regardless
          // of whether the player is on light or dark theme. For faded (non-
          // selected) chips, mute the number + border by blending them toward
          // the fill so they read dim, not sharp.
          const rawText = contrastTextColor(color)
          const rawOutline = rawText === '#000000' ? '#ffffff' : '#000000'
          const textColor = faded ? mixHex(rawText, color, 0.5) : rawText
          const outlineColor = faded ? mixHex(rawOutline, color, 0.5) : rawOutline
          const fontSize = Math.max(2, r * 0.88)
          const label = `+${region.bonus}`
          return (
            <G key={`region-bonus-${region.id}`} testID={state.map.territoryMarkers ? `region-bonus-${region.id}` : undefined} pointerEvents="none">
              <Circle
                cx={anchor.x}
                cy={anchor.y}
                r={r}
                fill={color}
                stroke={textColor}
                strokeWidth={strokeW}
              />
              {/* Two-pass text: opposite-coloured outline first, fill on
                  top. Same technique as the troop badges below — the
                  outline acts as a halo so the digit stays legible if
                  any other map layer paints into the badge circle. */}
              <SvgText
                x={anchor.x}
                y={anchor.y + fontSize * 0.36}
                fill={outlineColor}
                stroke={outlineColor}
                strokeWidth={fontSize * 0.32}
                strokeLinejoin="round"
                fontSize={fontSize}
                fontFamily={heavyNumberFont()}
                fontWeight="800"
                textAnchor="middle"
              >
                {label}
              </SvgText>
              <SvgText
                x={anchor.x}
                y={anchor.y + fontSize * 0.36}
                fill={textColor}
                fontSize={fontSize}
                fontFamily={heavyNumberFont()}
                fontWeight="800"
                textAnchor="middle"
              >
                {label}
              </SvgText>
            </G>
          )
        })}


        {/* Tap slop — see the tapSlopLayer memo for the rationale. Web keeps it
            in the board; native draws it in the top overlay (below). */}
        {Platform.OS === 'web' && tapSlopLayer}

        </G>
      </Svg>
        {/* Selection overlay, then effects — each its own <Svg> over the board so
            a selection, its fade, a tap flash or an attack animation redraws
            only that small layer, never the board (see MapOverlayLayer). They
            sit above everything on the board, troop badges included: the dim
            darkens an unrelated badge along with its territory. */}
        <MapOverlayLayer
          left={(containerWidth - svgWidth) / 2}
          width={svgWidth}
          height={svgHeight}
          viewBox={boardViewBox}
          transform={boardTransform}
          groupRef={selectionGroupRef}
          style={selectionFadeStyle}
          defs={shownSelection ? (
            <Defs>
              {overlayClipDefs(
                [shownSelection.selectedTerritoryId, shownSelection.secondaryTerritoryId, ...shownSelection.previewTargetIds],
                'sel-',
              )}
            </Defs>
          ) : undefined}
        >
          {shownSelection && (
            <>
              <SelectionDimLayer
                territoryIds={shownSelection.dimIds}
                markerCircles={state.map.territoryMarkers ? {
                  positionById: badgePositionById,
                  radius: territoryMarkerMetrics(state.map.territoryMarkers.radius, isLandscape, desktop).radius,
                } : undefined}
                territoryShapeById={territoryShapeById}
                extraShapes={logoShapes}
              />
              {selectedFillMode === 'lighten' && !hideBadges && !showRegionBonuses && shownSelection.selectedTerritoryId && (
                <SourceHatchLayer
                  shape={territoryShapeById[shownSelection.selectedTerritoryId]}
                  regionShape={territoryRegionShapeById[shownSelection.selectedTerritoryId]}
                  clipNS={selectionClipNS}
                  territoryId={shownSelection.selectedTerritoryId}
                  dark={shownSelection.sourceDark}
                  unitsPerPixel={glowUnitsPerPixel}
                  borderInset={territoryBorderWidth / 2}
                  regionBorderInset={regionBorderWidth / 2}
                />
              )}
              {!hideBadges && !showRegionBonuses && (innerGlowEnabled || ctfTargetCircles) && (
                <TargetHaloLayer
                  markerCircles={state.map.territoryMarkers ? {
                    positionById: badgePositionById,
                    radius: territoryMarkerMetrics(state.map.territoryMarkers.radius, isLandscape, desktop).radius,
                  } : undefined}
                  targetCircles={ctfTargetCircles ? {
                    positionById: badgePositionById,
                    radius: badgeRadius * 0.85,
                  } : undefined}
                  territoryIds={shownSelection.previewTargetIds}
                  sourceTerritoryId={selectedFillMode === 'lighten' ? shownSelection.selectedTerritoryId : undefined}
                  sourceColor={shownSelection.sourceGlowColor}
                  settledTerritoryIds={shownSelection.secondaryTerritoryId ? [shownSelection.secondaryTerritoryId] : []}
                  territoryShapeById={territoryShapeById}
                  territoryRegionShapeById={territoryRegionShapeById}
                  clipNS={selectionClipNS}
                  borderInset={territoryBorderWidth / 2}
                  regionBorderInset={regionBorderWidth / 2}
                  glowWidth={3 * glowUnitsPerPixel}
                />
              )}
            </>
          )}
        </MapOverlayLayer>
        {/* Live conquest flashes: each draws once into its own overlay and
            fades by native view opacity (see ConquestFlashLayer). */}
        <ConquestFlashOverlays
          events={events}
          slotByPlayerId={colorByPlayerId}
          playerColorHex={playerColorHex}
          shapeById={shapeById}
          strokeWidth={conquestFlashStrokeWidth}
          frame={transientFrame}
        />
        <MapOverlayLayer
          left={(containerWidth - svgWidth) / 2}
          width={svgWidth}
          height={svgHeight}
          viewBox={boardViewBox}
          transform={boardTransform}
          groupRef={effectsGroupRef}
          defs={pressedTarget ? <Defs>{overlayClipDefs([pressedTarget.id], 'fx-')}</Defs> : undefined}
        >
          {selectedFillMode === 'lighten' && !showRegionBonuses && (
            <SelectionEmphasisLayer
              clipNS={effectsClipNS}
              theme={theme}
              sourceStrokeColor={selectionSource?.normalStrokeColor}
              isDestination={pressedTarget?.id === secondaryTerritoryId}
              outerFlashWidth={regionBorderWidth + 4 * glowUnitsPerPixel}
              markerRadius={state.map.territoryMarkers ? territoryMarkerMetrics(state.map.territoryMarkers.radius, isLandscape, desktop).radius : undefined}
              badgeRadius={badgeRadius}
              // The phase owns tap validity. Only acknowledge a tap it accepted
              // as the source or destination; unrelated taps may still cancel.
              pressed={pressedTarget && (
                pressedTarget.id === selectedTerritoryId || pressedTarget.id === secondaryTerritoryId
              ) ? pressedTarget : undefined}
              pressNonce={pressFeedback?.nonce ?? 0}
            />
          )}
          {!markerMap && animationLayers}
        </MapOverlayLayer>
        {/* Troop numbers — the top layer, above selection glows and attack
            animations, as they have always been. On their own <Svg>, an army
            count changing redraws only the numbers, not the board. */}
        <MapOverlayLayer
          left={(containerWidth - svgWidth) / 2}
          width={svgWidth}
          height={svgHeight}
          viewBox={boardViewBox}
          transform={boardTransform}
          groupRef={badgesGroupRef}
          pointerEvents={state.map.territoryMarkers ? 'box-none' : 'none'}
          // Heavy-fog marker discs wear the same crosshatch as fogged land;
          // patterns are per-<Svg> on native, so this layer defines its own.
          defs={state.map.territoryMarkers && foggedIds.size > 0
            ? <Defs><FogHatchPattern id={`${clipNS}fog-hatch-disc`} gap={hatchGap * 0.45} background={HEAVY_FOG_FILL} color={HEAVY_FOG_LINE} /></Defs>
            : undefined}
        >
        {state.map.territoryMarkers && !showRegionBonuses && state.map.territories.map(territory => {
          // Heavy fog: the disc stays (a territory is there) but no owner is
          // known, so it is a grey disc under the fog crosshatch. Light fog
          // keeps the owner's colour. Either way no troop number (skipped below).
          const heavyFogged = territory.fogged === true && !territory.ownerId
          const pos = badgePositionById[territory.id] ?? territory.position
          return <TerritoryMarker key={`marker-${territory.id}`} id={territory.id}
            x={pos.x} y={pos.y} radius={state.map.territoryMarkers!.radius}
            landscape={isLandscape} desktop={desktop} dimmed={selectionDimmedTerritorySet.has(territory.id)}
            armed={selectedFillMode === 'lighten' && !hideBadges && (
              territory.id === selectedTerritoryId || territory.id === secondaryTerritoryId
            )}
            fill={heavyFogged
              ? `url(#${clipNS}fog-hatch-disc)`
              : opaqueMarkerFill(ownerFill(territory.ownerId, colorByPlayerId, palette.unowned))}
            onPress={onTerritoryPress ? handleTerritoryPress : undefined}
            pointer={showsPointer(territory.id)}
            label={territory.fogged ? `${territory.name}, hidden by fog` : `${territory.name}, ${territory.armies} armies`} />
        })}
          {!hideBadges && !showRegionBonuses &&
            state.map.territories.map((territory) => {
              // Fog of war: no troop count to show (light fog keeps the owner
              // colour on the territory or marker disc, never the number).
              if (territory.fogged) return null
              // Use the area only to choose between full-size and small-text
              // rendering. The threshold is intentionally set very low: we
              // always render a badge (every territory must show a troop
              // count). On tiny territories, badgeFontSize is already
              // sized relative to sizingWidth so it scales down automatically;
              // no badge is ever dropped outright.
              const area = areaById[territory.id] ?? 0
              // Scale factor: territories whose area is below 1/4 of the
              // standard badge area get a smaller font so the number fits
              // without clipping. The 0.25 multiplier was chosen so that
              // even the smallest Forbidden City cells still render a
              // legible 1-digit count (tested at mapW=1154).
              const isSmall =
                territory.shape && area > 0 && area < badgeFontSize * badgeFontSize * 0.55
              const effectiveFontSize = state.map.territoryMarkers
                ? territoryMarkerMetrics(state.map.territoryMarkers.radius, isLandscape, desktop,
                    String(armyOverrides?.[territory.id] ?? territory.armies)).fontSize
                : isSmall ? badgeFontSize * 0.72 : badgeFontSize

              // Mirror the fill-priority logic from the polygon loop so
              // the badge picks contrast against the *currently rendered*
              // fill (region-highlight > owner). Adjacent territories keep their
              // own fill now (hatch overlay marks them), so they contrast the same.
              const isInSelectedRegion =
                selectedRegionId !== undefined && territory.regionId === selectedRegionId
              const ownerColor = ownerFill(territory.ownerId, colorByPlayerId, palette.unowned)
              const visibleFill =
                !state.map.territoryMarkers && isInSelectedRegion && selectedRegionColor
                  ? selectedRegionColor
                  : ownerColor
              const textColor = contrastTextColor(visibleFill)
              const outlineColor = textColor === '#000000' ? '#ffffff' : '#000000'

              // react-native-svg's TextProps doesn't expose `paintOrder`,
              // so emulate the "stroke behind fill" outline effect by
              // rendering two text elements: a thicker stroked outline
              // first, then the solid-fill text on top.
              const overrideArmies = armyOverrides?.[territory.id]
              const hasPendingChange =
                overrideArmies !== undefined && overrideArmies !== territory.armies
              const label = String(overrideArmies ?? territory.armies)
              // Pending placements (reinforce phase, before commit) render
              // the badge in amber with a dark outline so the player can
              // see at a glance which territories have new troops staged
              // — the higher number alone isn't enough of a signal.
              const badgeFill = hasPendingChange ? SELECTED_STROKE : textColor
              const badgeOutline = hasPendingChange ? '#000000' : outlineColor
              // Use the precomputed corrected position (pole of inaccessibility
              // when the centroid falls outside the polygon, centroid otherwise).
              const bpos = badgePositionById[territory.id] ?? territory.position
              return (
                <TroopBadge
                  key={`label-${territory.id}`}
                  x={bpos.x}
                  y={bpos.y}
                  label={label}
                  fontSize={effectiveFontSize}
                  fill={badgeFill}
                  outline={badgeOutline}
                  outlineWidth={effectiveFontSize * (hasPendingChange ? 0.4 : 0.32)}
                  opacity={selectionDimmedTerritorySet.has(territory.id) ? 0.68 : 1}
                />
              )
            })}
        </MapOverlayLayer>
        {/* Fog of war: fog fading off territories that just came into view,
            above their colours and troop numbers (FogLiftLayer). */}
        <FogLiftLayer
          fogged={heavyFoggedIds}
          shapeOf={fogLiftShapeOf}
          frame={transientFrame}
          patternId={`${clipNS}fog-lift`}
          gap={hatchGap * 0.45}
        />
        {/* Attack results, above the troop numbers: every animation on
            circle-marker boards, and the "−N" loss labels on every board. */}
        <MapOverlayLayer
          left={(containerWidth - svgWidth) / 2}
          width={svgWidth}
          height={svgHeight}
          viewBox={boardViewBox}
          transform={boardTransform}
          groupRef={resultsGroupRef}
        >
          {markerMap && animationLayers}
          {attackLabelLayers}
        </MapOverlayLayer>
        {/* Native tap slop, on top so iOS asks it first ("aim at the small one,
            hit the small one"). Its clickable set changes with every selection;
            inside the board that change alone repainted the whole map. box-none:
            a tap on empty space falls through to the board underneath (Fabric
            returns no view when the <Svg> hit only itself). */}
        {Platform.OS !== 'web' && (
          <MapOverlayLayer
            left={(containerWidth - svgWidth) / 2}
            width={svgWidth}
            height={svgHeight}
            viewBox={boardViewBox}
            transform={boardTransform}
            groupRef={slopGroupRef}
            pointerEvents="box-none"
          >
            {tapSlopLayer}
          </MapOverlayLayer>
        )}

        {/* Right-edge overlay — pinned to the right of the map container
          (NOT the screen), vertically centred on the map's own height.
          Caller passes the player-roster strip here so it sits over the
          middle of the play surface regardless of how much vertical
          space the HUD takes below/beside the map. `box-none` lets taps
          fall through any empty area to the territories underneath
          while the overlay's own children remain tappable. */}
        {logoShapes.length > 0 && <WarfishClassicsAboutModal visible={aboutVisible} onClose={() => setAboutVisible(false)} />}
        {rightOverlay != null && (
        <View
          pointerEvents="box-none"
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            justifyContent: 'center',
          }}
        >
          {rightOverlay}
        </View>
      )}
        {/* Map key (MapDef.legend): always open, at a corner of the drawn map. */}
        {state.map.legend && legendInsets && (
          <MapLegend
            legend={state.map.legend}
            theme={theme === 'dark' ? 'dark' : 'light'}
            insets={legendInsets}
            centerTop={state.map.legend.centerY === undefined ? undefined
              : mapYToWrapper(state.map.legend.centerY, { y: mapY, width: mapW, height: mapH }, { width: svgWidth, height: svgHeight })}
            // Phones draw the map small: a tighter key keeps it off the board.
            compact={svgWidth < 600}
            darkSeaRoutes={!!state.map.darkSeaRoutes}
          />
        )}
        {(() => {
        // Anchor to the SVG's bounding box, not the container. The
        // wrapper centres the SVG horizontally when it's narrower than
        // the container (landscape letterboxing); vertical extent
        // already matches container height so top/bottom anchor cleanly.
        // Two slots so a MapDef can split independent chips across
        // different corners (e.g. zoom toggle in one corner, regions
        // toggle in another). Both anchor to the same SVG box.
        const sidePad = Math.max(0, (containerWidth - svgWidth) / 2)
        const inset = 8
        // Corner overlays hug the literal corner of the map wrapper.
        // Safe-area handling for these chips was tried in PR #339 and
        // reverted — the cog moved out of the old third overlay slot into the
        // landscape action-panel header, so the remaining slots (region
        // toggle, zoom toggle) just sit flush against the corner. The
        // map wrapper itself is padded by safe-area in MapView's
        // useMapDimensions opts, so these corners ARE already inside
        // the safe zone — adding inset padding here pushed them away
        // from the corner (regression reported in landscape feedback).
        function styleFor(
          corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right',
        ) {
          const cornerStyle: {
            top?: number
            bottom?: number
            left?: number
            right?: number
          } = {}
          if (corner === 'top-left') {
            cornerStyle.top = inset
            cornerStyle.left = sidePad + inset
          } else if (corner === 'top-right') {
            cornerStyle.top = inset
            cornerStyle.right = sidePad + inset
          } else if (corner === 'bottom-left') {
            cornerStyle.bottom = inset
            cornerStyle.left = sidePad + inset
          } else {
            cornerStyle.bottom = inset
            cornerStyle.right = sidePad + inset
          }
          return cornerStyle
        }
        return (
          <>
            {regionToggleNode && (
              <View
                pointerEvents="box-none"
                style={{ position: 'absolute', ...styleFor(regionToggleCorner), zIndex: 5 }}
              >
                {regionToggleNode}
              </View>
            )}
            {zoomToggleNode && (
              <View
                pointerEvents="box-none"
                style={{ position: 'absolute', ...styleFor(zoomToggleCorner), zIndex: 5 }}
              >
                {zoomToggleNode}
              </View>
            )}
            {/* On-map +/- zoom controls — bottom-centre of the SVG box, only
                while zoom mode is on and the caller wired onZoomScaleChange.
                Suppressed when an external control bar owns zoom. */}
            {zoomEnabled && onZoomScaleChange && !hideOnMapZoomButtons && (
              <View
                pointerEvents="box-none"
                style={{
                  position: 'absolute',
                  bottom: inset,
                  left: sidePad,
                  right: sidePad,
                  alignItems: 'center',
                  zIndex: 5,
                }}
              >
                <View style={{ flexDirection: 'row', gap: 10 }}>
                  <ZoomButton
                    label="−"
                    accessibilityLabel="Zoom out"
                    disabled={!canZoomOut}
                    onPress={() => handleZoomButton(-1)}
                  />
                  <ZoomButton
                    label="+"
                    accessibilityLabel="Zoom in"
                    disabled={!canZoomIn}
                    onPress={() => handleZoomButton(1)}
                  />
                </View>
              </View>
            )}
            {/* Drag-hint compass — briefly fades in over the map centre on
                zoom-in to signal the board is now pannable. Non-interactive. */}
            <Animated.View
              pointerEvents="none"
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: sidePad,
                right: sidePad,
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 6,
                opacity: dragHintOpacity,
              }}
            >
              <View
                style={{
                  width: 84,
                  height: 84,
                  borderRadius: 42,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text style={{ position: 'absolute', top: 4, color: '#fff', fontSize: 20 }}>↑</Text>
                <Text style={{ position: 'absolute', bottom: 4, color: '#fff', fontSize: 20 }}>↓</Text>
                <Text style={{ position: 'absolute', left: 8, color: '#fff', fontSize: 20 }}>←</Text>
                <Text style={{ position: 'absolute', right: 8, color: '#fff', fontSize: 20 }}>→</Text>
                <Text style={{ color: '#fff', fontSize: 10, fontWeight: '700', letterSpacing: 0.5 }}>
                  DRAG
                </Text>
              </View>
            </Animated.View>
          </>
        )
        })()}
      </View>
    </React.Profiler>
  )
}

/**
 * MapView is memoised so a PARENT re-render (game state tick, phase UI churn)
 * doesn't rebuild the SVG polygon tree when nothing it depends on changed.
 *
 * WHAT THIS MEMO DOES NOT DO — the previous comment here claimed it stopped
 * "orientation-change frames from useWindowDimensions" rebuilding the tree. It
 * cannot: `MapViewInner` calls `useMapDimensions()`, which calls
 * `useWindowDimensions()`, and `React.memo` only skips re-renders triggered by
 * the parent. A component's OWN hook subscriptions always re-render it. So any
 * dimension change re-renders this component whatever the comparator says.
 *
 * How bad that is has never been measured. React Native's `useWindowDimensions`
 * already skips identical values, so the cost is the number of DISTINCT sizes
 * iOS reports per rotation, which may well be 1-2. Measure before "fixing" it —
 * see docs/map-perf.md → "Render-path audit" (P1).
 *
 * The default shallow comparator is otherwise right for the parent-driven case.
 * ReactNode overlay props (regionToggleNode, rightOverlay, …) are new JSX
 * references whenever the parent renders and so always fail shallow equality;
 * on the in-game path they are currently `null`, so they cost nothing today.
 */
export const MapView = React.memo(MapViewInner, (prev, next) =>
  mapViewPropsEqual(
    prev as unknown as Record<string, unknown>,
    next as unknown as Record<string, unknown>,
  ),
)
